import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChatContext } from './chatContext.js';
import { useChatClients } from '../hooks/useChatClients.jsx';
import useAttendantWebSocket from '../hooks/useAttendantWebSocket.jsx';
import { useNotificationGate } from '../hooks/useNotificationGate.js';
import {
  getAttendantMe,
  getCompanyById,
  getRoleById,
  listInternalChatAttendants,
  getInternalChatUnreadSummary,
  getSession,
} from '../api/api.js';
import { writeAttendantCache } from '../utils/cache.js';
import { resolveClientDisplayName } from '../utils/chatDisplayName.js';
import { formatBrazilianPhone } from '../utils/phone.js';
import {
  getConversationContact,
  getConversationContactDigits,
} from '../utils/conversationContact.js';

import { resolveMediaUrl } from '../utils/media.js';

import {
  getMessageActivityTimestamp,
  normalizeConversationSortMode,
  normalizeConversationId,
  resolveConversationUnreadCount,
  isConversationAwaitingEvaluation,
} from '../utils/conversationUtils.js';
import { isClientMessage } from '../utils/chatMessage.js';
import { trackAnalyticsEvent } from '../utils/analytics.js';

import {
  playNewClientNotificationSound,
  playNewMessageNotificationSound,
  playInternalChatMessageNotificationSound,
  isNotificationSupported,
  stopNewClientNotificationSound,
} from '../utils/notifications.js';
import {
  LOGOUT_EVENT,
  getAuthSession,
  hasReadableSessionCookie,
  performLogout,
  setAuthRoleSnapshot,
  setAuthSession,
} from '../utils/auth.js';
import {
  ensureSessionHealthy,
  getSessionHealthSnapshot,
  SESSION_HEALTH_EVENT,
} from '../utils/sessionManager.js';
import { openInternalChat } from '../utils/internalChatLauncher.js';
import { dispatchInternalChatEvent } from '../utils/internalChatEvents.js';
import {
  isInternalChatAuthCloseEvent,
  isSessionHealthAuthFailure,
} from '../utils/internalChatWebSocket.js';
import {
  incrementInternalChatUnreadThread,
  replaceInternalChatUnreadMap,
} from '../utils/internalChatUnread.js';
import { attendantHasPermission, resolveAdminPrivileges } from '../utils/role.js';
import { WS_BASE_URL } from '../config.js';
import { showWarning } from '../utils/toast.js';
import { getSafeErrorDetails, logSafeError, logSafeWarning } from '../utils/safeConsole.js';

const EVALUATION_HANDLING_STATUSES = ['waiting_evaluation', 'waiting_evaluation_comment'];
const INTERNAL_TRANSFER_REQUEST_TITLE = 'solicitação de transferência';
const ADMIN_SCOPE_STORAGE_KEY = 'chat:admin-conversations-scope';
const CHAT_SORT_MODE_STORAGE_KEY = 'chat_sort_mode';
const CHAT_LS_LAST_OPEN_KEY = 'chat_last_open_v1';
const MESSAGE_DEDUP_STORAGE_KEY = 'bluezap:message-notify-dedupe-v1';
const MESSAGE_DEDUP_TTL_MS = 60 * 1000;
const UNREAD_DEDUP_TTL_MS = 5 * 60 * 1000;
const MAX_INCOMING_UNREAD_ENTRIES = 250;
const MAX_UNREAD_DEDUP_ENTRIES = 500;
const REALTIME_NOTIFICATION_GRACE_MS = 1500;
const MAX_INTERNAL_ATTENDANCE_MENTIONS = 100;
const NOTIFICATION_PERMISSION_WARNING_KEY = 'bluezap:notification-permission-warning-v1';
const INTERNAL_CHAT_RECONNECT_BASE_DELAY_MS = 5000;
const INTERNAL_CHAT_RECONNECT_JITTER_RATIO = 0.2;
const INTERNAL_CHAT_UNREAD_SYNC_INTERVAL_MS = 10 * 1000;
const INTERNAL_CHAT_WS_TAB_ID_KEY = 'bluezap:internal-chat-ws-tab-id';
const INTERNAL_CHAT_WS_NORMAL_CLOSE_CODES = new Set([1000, 1001]);
const CHAT_CLIENTS_DISABLED_ROUTE_PREFIXES = [
  '/workflows',
  '/workflow-actions',
  '/webhooks',
  '/companyRegister',
  '/client-updates',
  '/super-admin/agents',
];

const normalizeOptionalNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const readLocalJsonMap = (key) => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeLocalJsonMap = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value || {}));
  } catch {
    // Storage local e auxiliar; ignoramos falhas de persistencia.
  }
};

const applyReconnectJitter = (delay, ratio = INTERNAL_CHAT_RECONNECT_JITTER_RATIO) => {
  const safeDelay = Number.isFinite(delay) && delay > 0 ? delay : INTERNAL_CHAT_RECONNECT_BASE_DELAY_MS;
  const jitterWindow = Math.max(250, Math.floor(safeDelay * ratio));
  return safeDelay + Math.floor(Math.random() * jitterWindow);
};

const buildInternalChatTabId = () => {
  const randomPart =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `tab-${Date.now().toString(36)}-${randomPart}`;
};

const getInternalChatTabId = () => {
  if (typeof window === 'undefined') return buildInternalChatTabId();
  try {
    const existing = window.sessionStorage.getItem(INTERNAL_CHAT_WS_TAB_ID_KEY);
    if (existing) return existing;
    const next = buildInternalChatTabId();
    window.sessionStorage.setItem(INTERNAL_CHAT_WS_TAB_ID_KEY, next);
    return next;
  } catch {
    return buildInternalChatTabId();
  }
};

const getInternalChatWsEnvironment = () => ({
  page_url:
    typeof window !== 'undefined' && window.location?.href
      ? window.location.href
      : null,
  visibility_state:
    typeof document !== 'undefined' && document.visibilityState
      ? document.visibilityState
      : null,
  online_state:
    typeof navigator !== 'undefined' && 'onLine' in navigator
      ? Boolean(navigator.onLine)
      : null,
});

const logInternalChatWs = (level, message, details = {}) => {
  const method =
    level === 'error'
      ? 'error'
      : level === 'warning'
        ? 'warn'
        : 'info';
  console[method](`[WS InternalChat] ${message}`, details);
};

const isExpectedInternalChatWsClose = (event, cleanupReason) => {
  const closeCode = Number(event?.code || 0);
  return Boolean(cleanupReason) && INTERNAL_CHAT_WS_NORMAL_CLOSE_CODES.has(closeCode);
};

const resolveMessageKey = (message) => {
  if (!message || typeof message !== 'object') return null;
  const idCandidates = [
    message.id,
    message.message_id,
    message.messageId,
    message.key?.id,
    message.baileys_message_id,
    message.baileysMessageId,
  ];
  for (const candidate of idCandidates) {
    if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
      return `id:${candidate}`;
    }
  }
  const mediaCandidates = [
    message.media_url,
    message.mediaUrl,
    message.mediaURI,
    message.mediaUri,
    message.media_path,
    message.mediaPath,
  ];
  for (const candidate of mediaCandidates) {
    if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
      return `media:${candidate}`;
    }
  }
  const ts = getMessageActivityTimestamp(message);
  if (ts) {
    const sender = message.sender || message.sender_id || message.author_id || '';
    return `ts:${ts}:${sender}`;
  }
  return null;
};

const readMessageDedupeMap = () => {
  try {
    const raw = window.localStorage.getItem(MESSAGE_DEDUP_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeMessageDedupeMap = (map) => {
  try {
    window.localStorage.setItem(MESSAGE_DEDUP_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage local e auxiliar; ignoramos falhas de persistencia.
  }
};

const pruneMessageDedupeMap = (map, now) => {
  let changed = false;
  Object.keys(map).forEach((key) => {
    const entry = map[key];
    const ts = Number(entry?.ts || 0);
    if (!ts || now - ts > MESSAGE_DEDUP_TTL_MS) {
      delete map[key];
      changed = true;
    }
  });
  return changed;
};

const isNotificationDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('notification_debug') === 'true';
  } catch {
    return false;
  }
};

const debugNotificationFlow = (event, details = {}) => {
  if (!isNotificationDebugEnabled()) return;
  // Guardado por flag para diagnostico pontual sem ruido em producao.
  console.debug('[notification-flow]', event, details);
};

const hasMessageDisplayContent = (message) => {
  if (!message || typeof message !== 'object') return false;
  if (extractMessagePreview(message)) return true;
  return Boolean(
    message.media_url ||
      message.mediaUrl ||
      message.mediaURI ||
      message.mediaUri ||
      message.media_path ||
      message.mediaPath ||
      message.file_name ||
      message.fileName ||
      message.shared_contact ||
      message.sharedContact
  );
};

const isStatusOnlyMessage = (message) => {
  if (!message || typeof message !== 'object') return false;
  const type = String(message.type || message.message_type || message.messageType || '').toLowerCase();
  if (
    type.includes('receipt') ||
    type.includes('status_update') ||
    type === 'status'
  ) {
    return true;
  }
  const status = String(message.status || '').trim().toLowerCase();
  return ['read', 'played', 'delivered'].includes(status) && !hasMessageDisplayContent(message);
};

const normalizeConversationEventType = (payload) => {
  if (!payload || typeof payload !== 'object') return '';
  return String(payload.type ?? payload.event ?? '').trim().toLowerCase();
};

const getWsSlotPayload = (payload) => payload?.slot || payload?.conversation || null;

const getWsSlotPayloadId = (slotPayload, payloadMessage = null, payload = null) =>
  slotPayload?.id ??
  slotPayload?.slot_id ??
  slotPayload?.slotId ??
  slotPayload?.conversation_id ??
  slotPayload?.conversationId ??
  payloadMessage?.conversation_id ??
  payloadMessage?.conversationId ??
  payloadMessage?.chat_id ??
  payloadMessage?.chatId ??
  payload?.conversation_id ??
  payload?.conversationId ??
  payload?.chat_id ??
  payload?.chatId ??
  null;

const extractPayloadMessage = (payload, slotPayload = null) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.message && typeof payload.message === 'object') return payload.message;
  if (Array.isArray(payload.messages)) {
    return payload.messages.find((message) => message && typeof message === 'object') || null;
  }
  const slotMessages = Array.isArray(slotPayload?.messages) ? slotPayload.messages : [];
  return slotMessages.length ? slotMessages[slotMessages.length - 1] : null;
};

const resolveIncomingPayloadMessageKey = (payload, slotPayload, message) => {
  const directMessageKey = resolveMessageKey(message);
  if (directMessageKey) return directMessageKey;

  const idCandidates = [
    payload?.message_id,
    payload?.messageId,
    payload?.MessageId,
    payload?.baileys_message_id,
    payload?.baileysMessageId,
    payload?.message_key_id,
    payload?.messageKeyId,
  ];
  for (const candidate of idCandidates) {
    if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
      return `id:${candidate}`;
    }
  }

  const timestamp =
    payload?.timestamp ??
    payload?.created_at ??
    payload?.createdAt ??
    slotPayload?.last_message_at ??
    slotPayload?.lastMessageAt ??
    slotPayload?.updated_at ??
    slotPayload?.updatedAt ??
    null;
  if (timestamp !== null && timestamp !== undefined && String(timestamp).trim()) {
    const unreadCount = slotPayload?.unread_count ?? slotPayload?.unreadCount ?? '';
    return `ts:${timestamp}:${unreadCount}`;
  }

  return null;
};

const isEvaluationPayload = (slotPayload, message) => {
  const handlingStatus = String(slotPayload?.handling_status ?? slotPayload?.status ?? '').toLowerCase();
  const messageHandlingStatus = String(message?.handling_status ?? '').toLowerCase();
  return EVALUATION_HANDLING_STATUSES.some(
    (status) => handlingStatus.includes(status) || messageHandlingStatus.includes(status)
  );
};

const shouldNotifyIncomingMessage = (previousMeta, nextMeta, context = {}) => {
  const debugDetails = {
    chatId: context.chatId ?? null,
    previousKey: previousMeta?.key || null,
    nextKey: nextMeta?.key || null,
    previousTs: previousMeta?.ts || null,
    nextTs: nextMeta?.ts || null,
  };

  if (context.initialSnapshot) {
    return { notify: false, reason: 'initial_snapshot', debugDetails };
  }
  if (!context.isClient) {
    return { notify: false, reason: 'from_me', debugDetails };
  }
  if (context.isStatusEvent) {
    return { notify: false, reason: 'status_event', debugDetails };
  }
  if (context.isEvaluation) {
    return { notify: false, reason: 'evaluation_message', debugDetails };
  }
  if (context.isConversationOpen) {
    return { notify: false, reason: 'conversation_open', debugDetails };
  }
  if (context.alreadyNotified) {
    return { notify: false, reason: 'duplicate_message', debugDetails };
  }

  const nextKey = nextMeta?.key || null;
  const prevKey = previousMeta?.key || null;
  const nextTs = Number(nextMeta?.ts || 0);
  const prevTs = Number(previousMeta?.ts || 0);
  const hasNextTs = Number.isFinite(nextTs) && nextTs > 0;
  const hasPrevTs = Number.isFinite(prevTs) && prevTs > 0;

  if (!nextKey && !hasNextTs) {
    return { notify: false, reason: 'no_message_key', debugDetails };
  }

  if (nextKey && prevKey) {
    return nextKey !== prevKey
      ? { notify: true, reason: 'message_key_changed', debugDetails }
      : { notify: false, reason: 'duplicate_message', debugDetails };
  }

  if (nextKey && !prevKey) {
    return { notify: true, reason: 'new_message_key', debugDetails };
  }

  if (hasNextTs && hasPrevTs) {
    return nextTs !== prevTs
      ? { notify: true, reason: 'timestamp_changed', debugDetails }
      : { notify: false, reason: 'duplicate_message', debugDetails };
  }

  return { notify: true, reason: 'new_timestamp', debugDetails };
};

const resolveNotificationSortTs = (primary, fallback = null) => {
  const primaryTs = getMessageActivityTimestamp(primary);
  if (primaryTs) return primaryTs;
  const fallbackTs = getMessageActivityTimestamp(fallback);
  if (fallbackTs) return fallbackTs;
  return Date.now();
};

const normalizeAdminScope = (value) => {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return raw === 'all' ? 'all' : 'attendant';
};

const getAdminScopeStorageKey = (companyId) => {
  const normalizedCompanyId = normalizeOptionalNumber(companyId);
  return normalizedCompanyId !== null
    ? `${ADMIN_SCOPE_STORAGE_KEY}:${normalizedCompanyId}`
    : ADMIN_SCOPE_STORAGE_KEY;
};

const readStoredAdminScope = (companyId = null) => {
  try {
    const key = getAdminScopeStorageKey(companyId);
    const stored = window.localStorage.getItem(key);
    if (stored) return normalizeAdminScope(stored);

    // Compatibilidade: tenta chave antiga (sem company_id) e propaga para a nova
    if (key !== ADMIN_SCOPE_STORAGE_KEY) {
      const legacy = window.localStorage.getItem(ADMIN_SCOPE_STORAGE_KEY);
      if (legacy) {
        const normalized = normalizeAdminScope(legacy);
        window.localStorage.setItem(key, normalized);
        return normalized;
      }
    }

    return 'attendant';
  } catch {
    return 'attendant';
  }
};

const readStoredChatSortMode = () => {
  try {
    return normalizeConversationSortMode(
      window.localStorage.getItem(CHAT_SORT_MODE_STORAGE_KEY)
    );
  } catch {
    return 'lastMessage';
  }
};

const readAuthSnapshot = () => getAuthSession();

const PUBLIC_AUTH_PATHS = new Set(['/login', '/forgot-password', '/reset-password']);

const isPublicAuthRoute = () => {
  if (typeof window === 'undefined') return false;
  const rawPathname = typeof window.location?.pathname === 'string' ? window.location.pathname : '';
  const normalizedPathname = rawPathname.replace(/\/+$/, '') || '/';
  return PUBLIC_AUTH_PATHS.has(normalizedPathname);
};

const buildBootstrapKey = (hasCookie, attendantIdValue, companyIdValue) =>
  [
    hasCookie ? 'cookie:1' : 'cookie:0',
    normalizeOptionalNumber(attendantIdValue) ?? 'attendant:null',
    normalizeOptionalNumber(companyIdValue) ?? 'company:null',
  ].join('|');

const buildNotificationPermissionWarningKey = (attendantIdValue, companyIdValue) =>
  [
    NOTIFICATION_PERMISSION_WARNING_KEY,
    normalizeOptionalNumber(attendantIdValue) ?? 'attendant:null',
    normalizeOptionalNumber(companyIdValue) ?? 'company:null',
  ].join('|');

const resolveContactNumber = (entry) => {
  const digits = getConversationContactDigits(entry);
  return digits ? formatBrazilianPhone(digits) : '';
};

const extractMessagePreview = (message) => {
  const candidates = [
    message?.message,
    message?.text,
    message?.body,
    message?.content,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return '';
};

const getLastClientMessagePreview = (entry) => {
  const messages = Array.isArray(entry?.messages) ? entry.messages : [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!message) continue;
    if (!isClientMessage(message)) continue;
    const preview = extractMessagePreview(message);
    if (preview) {
      return preview;
    }
  }
  return '';
};

const parseAttendanceMentionFromInternalMessage = (messageText) => {
  if (typeof messageText !== 'string') return null;
  const normalized = messageText.trim();
  if (!normalized) return null;
  const idMatch = normalized.match(/\bAtendimento\s*#\s*(\d+)\b/i);
  const chatId = idMatch ? Number(idMatch[1]) : null;
  return {
    chatId: Number.isFinite(chatId) ? chatId : null,
  };
};

const parseTransferRequestFromInternalMessage = (messageText) => {
  if (typeof messageText !== 'string') return null;
  const normalized = messageText.trim();
  if (!normalized) return null;
  const firstLine = normalized.split('\n', 1)[0]?.trim().toLowerCase() || '';
  if (firstLine !== INTERNAL_TRANSFER_REQUEST_TITLE) return null;
  return parseAttendanceMentionFromInternalMessage(messageText);
};

const getPrimaryClientContact = (client) => {
  if (!client) return null;
  const contacts = Array.isArray(client.contacts) ? client.contacts.filter(Boolean) : [];
  if (contacts.length > 0) return contacts[0];
  return getConversationContact(client);
};

const resolveClientAvatarUrl = (client) => {
  if (!client || typeof client !== 'object') return '';
  const primaryContact = getPrimaryClientContact(client);

  const candidateKeys = [
    'photograph',
    'photo',
    'avatar',
    'picture',
    'image',
    'photo_url',
    'photoUrl',
    'avatar_url',
    'avatarUrl',
    'image_url',
    'imageUrl',
    'profile_photo',
    'profilePhoto',
    'profile_picture',
    'profilePicture',
    'contact_photo',
    'client_photo',
    'customer_photo',
    'contact_avatar_url',
    'customer_avatar_url',
  ];

  const sources = [
    primaryContact,
    client,
  ].filter(Boolean);

  for (const source of sources) {
    for (const key of candidateKeys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        return resolveMediaUrl(value);
      }
    }
  }

  return '';
};

const INTERNAL_CHAT_PHOTO_CANDIDATE_KEYS = [
  'photo',
  'photograph',
  'avatar',
  'picture',
  'photo_url',
  'photoUrl',
  'photograph_url',
  'avatar_url',
  'avatarUrl',
  'profile_photo',
  'profilePhoto',
  'profile_picture',
  'profilePicture',
  'image',
  'image_url',
  'imageUrl',
];

const resolveInternalChatAvatarUrl = (participant) => {
  if (!participant || typeof participant !== 'object') return '';
  for (const key of INTERNAL_CHAT_PHOTO_CANDIDATE_KEYS) {
    const value = participant[key];
    if (value) return resolveMediaUrl(value);
  }
  const nestedSources = [participant.user, participant.profile];
  for (const source of nestedSources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of INTERNAL_CHAT_PHOTO_CANDIDATE_KEYS) {
      const value = source[key];
      if (value) return resolveMediaUrl(value);
    }
  }
  return '';
};

export function ChatProvider({ children }) {
  const [pathname, setPathname] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.location?.pathname || '';
  });
  const [attendantData, setAttendantData] = useState(() => {
    return null;
  });
  const [companySettings, setCompanySettings] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(null);
  const [isAgent, setIsAgent] = useState(false);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [sessionHealth, setSessionHealth] = useState(() => getSessionHealthSnapshot());
  const [authState, setAuthState] = useState(() => readAuthSnapshot());
  const [adminConversationScope, setAdminConversationScopeState] = useState(() =>
    readStoredAdminScope(readAuthSnapshot().companyId)
  );
  const [chatSortMode, setChatSortModeState] = useState(() => readStoredChatSortMode());
  const [internalChatAttendants, setInternalChatAttendants] = useState([]);
  const [internalChatAttendantsLoading, setInternalChatAttendantsLoading] = useState(false);
  const [internalChatPresence, setInternalChatPresence] = useState({});
  const [internalChatAttendanceMentions, setInternalChatAttendanceMentions] = useState({});
  const [whatsappPresence, setWhatsappPresence] = useState({});
  const [isAttendantWsConnected, setIsAttendantWsConnected] = useState(false);
  const internalChatWsRef = useRef(null);
  const internalChatWsMetaRef = useRef(new WeakMap());
  const internalChatWsAttemptRef = useRef(0);
  const internalChatWsInstanceSeqRef = useRef(0);
  const internalChatWsRuntimeRef = useRef({});
  const internalChatWsPageLifecycleRef = useRef(null);
  const internalChatWsClientIdRef = useRef(getInternalChatTabId());
  const internalChatWsFailedAttemptsRef = useRef(0);
  const internalChatUnreadSyncInFlightRef = useRef(null);
  const attendantWsHandlerRef = useRef(null);
  const applyIncomingPayloadRef = useRef(null);
  const activeChatIdRef = useRef(null);
  const internalChatNotificationPrefsRef = useRef({
    allowPopupNotification: false,
    allowSoundNotification: false,
    attendantId: null,
    isAdmin: false,
    adminScope: 'attendant',
  });
  const prevAuthRef = useRef({ attendantId: null, companyId: null });
  const bootstrapKeyRef = useRef(null);
  const bootstrapInFlightRef = useRef(false);
  const internalChatAttendantsLoadedCompanyRef = useRef(null);
  const internalChatAttendantsInFlightRef = useRef(null);
  const unreadDedupeRef = useRef(new Map());
  const [lastOpenMap, setLastOpenMap] = useState(() => readLocalJsonMap(CHAT_LS_LAST_OPEN_KEY));
  const [incomingUnreadMap, setIncomingUnreadMap] = useState({});
  const { emitPopupNotification } = useNotificationGate();

  const attendantId = authState.attendantId ?? null;

  const normalizedAttendantId = useMemo(
    () => normalizeOptionalNumber(authState.attendantId ?? attendantData?.id ?? null),
    [authState.attendantId, attendantData]
  );

  const effectiveCompanyId = useMemo(
    () => authState.companyId ?? attendantData?.company_id ?? null,
    [authState.companyId, attendantData]
  );

  const normalizedCompanyId = useMemo(
    () => normalizeOptionalNumber(effectiveCompanyId),
    [effectiveCompanyId]
  );
  const hasCompany = normalizedCompanyId !== null;
  const isSessionOffline =
    sessionHealth?.sessionHealth === 'unhealthy' && sessionHealth?.reasonType === 'network';
  const isSessionInvalid =
    sessionHealth?.sessionHealth === 'unhealthy' && sessionHealth?.reasonType === 'auth';
  const canAccessInternalChat = useMemo(() => {
    if (!hasCompany) return false;
    if (isSuperAdmin) return false;
    return (
      Boolean(isAdmin) ||
      Boolean(isAgent) ||
      attendantHasPermission(attendantData, 'internal_chat:access')
    );
  }, [attendantData, hasCompany, isAdmin, isAgent, isSuperAdmin]);
  const hasAllTeamConversationAccess = Boolean(
    attendantData?.has_all_team_conversaion_access
  );
  const attendantTeamIds = useMemo(() => {
    const teamIds = new Set();

    if (Array.isArray(attendantData?.team_ids)) {
      attendantData.team_ids.forEach((teamId) => {
        const normalized = normalizeOptionalNumber(teamId);
        if (normalized !== null) teamIds.add(normalized);
      });
    }

    if (Array.isArray(attendantData?.teams)) {
      attendantData.teams.forEach((team) => {
        const normalized = normalizeOptionalNumber(team?.id);
        if (normalized !== null) teamIds.add(normalized);
      });
    }

    const singleTeamId = normalizeOptionalNumber(attendantData?.team_id);
    if (singleTeamId !== null) {
      teamIds.add(singleTeamId);
    }

    return Array.from(teamIds.values()).sort((a, b) => a - b);
  }, [attendantData]);

  useEffect(() => {
    setInternalChatAttendanceMentions({});
  }, [normalizedCompanyId, normalizedAttendantId]);

  useEffect(() => {
    setInternalChatAttendants([]);
    setInternalChatAttendantsLoading(false);
    setInternalChatPresence({});
    internalChatAttendantsLoadedCompanyRef.current = null;
    internalChatAttendantsInFlightRef.current = null;
  }, [normalizedCompanyId]);

  const applyRoleSnapshot = useCallback((roleNameValue, roleIdValue) => {
    setAuthRoleSnapshot(roleNameValue, roleIdValue);
    setAuthState((prev) => {
      const normalizedRoleName = roleNameValue || null;
      const normalizedRoleId =
        roleIdValue !== null && roleIdValue !== undefined ? String(roleIdValue) : null;
      if (prev.roleName === normalizedRoleName && prev.roleId === normalizedRoleId) {
        return prev;
      }
      return {
        ...prev,
        roleName: normalizedRoleName,
        roleId: normalizedRoleId,
      };
    });
  }, []);

  useEffect(() => {
    if (authState.roleName === undefined || authState.roleName === null) {
      return;
    }
    const {
      isAdmin: resolvedAdmin,
      isSuperAdmin: resolvedSuperAdmin,
      isAgent: resolvedAgent,
      isGlobalAdmin: resolvedGlobalAdmin,
    } = resolveAdminPrivileges(
      attendantData,
      authState.roleName,
      attendantData?.role_id ?? attendantData?.role?.id ?? null
    );
    setIsSuperAdmin(resolvedSuperAdmin);
    setIsAgent(resolvedAgent);
    setIsGlobalAdmin(resolvedGlobalAdmin);
    setIsAdmin(resolvedAdmin);
  }, [authState.roleName, attendantData]);

  // Admin pode alternar entre fila propria e todas as filas.
  const effectiveAdminScope = isAdmin ? adminConversationScope : 'attendant';
  const adminShowAllConversations = effectiveAdminScope === 'all';
  const conversationScope = effectiveAdminScope;

  const chatClientsEnabled = (() => {
    if (typeof window === 'undefined') return false;
    // Restore the authenticated profile before classifying queues. During a
    // reload localStorage already has IDs, but teams and privileges are unknown.
    if (!isAuthReady || !attendantData || normalizedAttendantId === null) return false;
    const disabledRoute = CHAT_CLIENTS_DISABLED_ROUTE_PREFIXES.some((routePrefix) =>
      pathname.startsWith(routePrefix)
    );
    return normalizedCompanyId !== null && !disabledRoute;
  })();
  const chatRealtimeEnabled = chatClientsEnabled;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncPathname = () => {
      setPathname(window.location?.pathname || '');
    };

    const dispatchLocationChange = () => {
      window.dispatchEvent(new Event('bluezap:location-change'));
    };

    const wrapHistoryMethod = (methodName) => {
      const historyRef = window.history;
      const original = historyRef[methodName];
      if (typeof original !== 'function') return () => {};

      const wrapped = function wrappedHistoryMethod(...args) {
        const result = original.apply(historyRef, args);
        dispatchLocationChange();
        return result;
      };

      historyRef[methodName] = wrapped;
      return () => {
        historyRef[methodName] = original;
      };
    };

    const restorePushState = wrapHistoryMethod('pushState');
    const restoreReplaceState = wrapHistoryMethod('replaceState');

    window.addEventListener('popstate', syncPathname);
    window.addEventListener('hashchange', syncPathname);
    window.addEventListener('bluezap:location-change', syncPathname);
    syncPathname();

    return () => {
      window.removeEventListener('popstate', syncPathname);
      window.removeEventListener('hashchange', syncPathname);
      window.removeEventListener('bluezap:location-change', syncPathname);
      restorePushState();
      restoreReplaceState();
    };
  }, []);

  const loadInternalChatAttendants = useCallback(async ({ force = false } = {}) => {
    if (normalizedCompanyId === null) {
      setInternalChatAttendants([]);
      setInternalChatAttendantsLoading(false);
      internalChatAttendantsLoadedCompanyRef.current = null;
      internalChatAttendantsInFlightRef.current = null;
      return [];
    }

    if (
      !force &&
      internalChatAttendantsLoadedCompanyRef.current === normalizedCompanyId &&
      internalChatAttendants.length > 0
    ) {
      return internalChatAttendants;
    }

    if (!force && internalChatAttendantsInFlightRef.current) {
      return internalChatAttendantsInFlightRef.current;
    }

    const request = (async () => {
      setInternalChatAttendantsLoading(true);
      if (force) {
        internalChatAttendantsLoadedCompanyRef.current = null;
      }
      try {
        const data = await listInternalChatAttendants();
        const attendants = data?.attendants || [];
        setInternalChatAttendants(attendants);
        internalChatAttendantsLoadedCompanyRef.current = normalizedCompanyId;
        if (Array.isArray(attendants)) {
          const updatedPresence = {};
          attendants.forEach((attendant) => {
            updatedPresence[attendant.id] = !!attendant.is_online;
          });
          setInternalChatPresence((prev) => ({ ...prev, ...updatedPresence }));
        }
        return attendants;
      } catch (err) {
        logSafeError('Não foi possível carregar a lista de atendentes do chat interno.', err);
        throw err;
      } finally {
        setInternalChatAttendantsLoading(false);
        if (internalChatAttendantsInFlightRef.current === request) {
          internalChatAttendantsInFlightRef.current = null;
        }
      }
    })();

    internalChatAttendantsInFlightRef.current = request;

    try {
      return await request;
    } catch {
      return [];
    }
  }, [internalChatAttendants, normalizedCompanyId]);

  const ensureInternalChatAttendantsLoaded = useCallback(async () => {
    return loadInternalChatAttendants({ force: false });
  }, [loadInternalChatAttendants]);

  const applyAttendantData = useCallback(
    async (me) => {
      if (!me) {
        setAttendantData(null);
        setIsSuperAdmin(false);
        setIsAgent(false);
        setIsGlobalAdmin(false);
        setIsAdmin(false);
        applyRoleSnapshot(null, null);
        return null;
      }

      const resolvedAttendantId = normalizeOptionalNumber(me?.id ?? me?.attendant_id ?? null);
      const rawCompanyId = me?.company_id ?? me?.companyId;
      const resolvedCompanyId =
        rawCompanyId === undefined ? undefined : normalizeOptionalNumber(rawCompanyId);
      const nextSessionPatch = {
        attendantId: resolvedAttendantId,
      };
      // Em contas Super Admin, /attendants/me pode vir sem company_id.
      // Nesse caso preservamos a empresa atualmente selecionada no contexto.
      if (resolvedCompanyId !== undefined && resolvedCompanyId !== null) {
        nextSessionPatch.companyId = resolvedCompanyId;
      }
      setAuthSession(nextSessionPatch);

      setAttendantData(me);
      writeAttendantCache(me);

      let resolvedRoleId = me?.role_id ?? me?.role?.id ?? null;
      let resolvedRoleName = me?.role?.name ?? me?.role_name ?? '';

      if (!resolvedRoleName && resolvedRoleId) {
        try {
          const role = await getRoleById(resolvedRoleId);
          resolvedRoleName = role?.name ?? '';
        } catch (roleErr) {
          logSafeWarning('Erro ao obter papel do atendente.', roleErr);
        }
      }

      applyRoleSnapshot(resolvedRoleName || null, resolvedRoleId);

      const {
        isAdmin: resolvedAdmin,
        isSuperAdmin: resolvedSuperAdmin,
        isAgent: resolvedAgent,
        isGlobalAdmin: resolvedGlobalAdmin,
      } = resolveAdminPrivileges(
        me,
        resolvedRoleName
      );
      setIsSuperAdmin(resolvedSuperAdmin);
      setIsAgent(resolvedAgent);
      setIsGlobalAdmin(resolvedGlobalAdmin);
      setIsAdmin(resolvedAdmin);

      return me;
    },
    [applyRoleSnapshot]
  );

  const refreshAttendantData = useCallback(async (options = {}) => {
    try {
      const me = await getAttendantMe(null, options);
      return await applyAttendantData(me);
    } catch (err) {
      logSafeError('Erro ao atualizar dados do atendente.', err);
      return null;
    }
  }, [applyAttendantData]);

  const refreshSession = useCallback(async () => {
    try {
      const session = await getSession();
      const storedSession = getAuthSession();
      const resolvedCompanyId =
        session?.company_id === null || session?.company_id === undefined
          ? storedSession?.companyId ?? null
          : session.company_id;

      const sessionRoleName =
        typeof session?.role === 'string' && session.role.trim() ? session.role : null;
      const sessionRoleId = session?.role_id ?? null;
      const shouldUpdateRole = sessionRoleName !== null || sessionRoleId !== null;
      const {
        isAdmin: sessionIsAdmin,
        isSuperAdmin: sessionIsSuperAdmin,
        isAgent: sessionIsAgent,
        isGlobalAdmin: sessionIsGlobalAdmin,
      } =
        resolveAdminPrivileges({}, sessionRoleName, sessionRoleId);

      setAuthSession({
        attendantId: session?.attendant_id,
        companyId: resolvedCompanyId,
        roleName: sessionRoleName,
        roleId: sessionRoleId,
      });
      setAuthState((prev) => ({
        ...prev,
        attendantId: session?.attendant_id ?? null,
        companyId: resolvedCompanyId ?? null,
        roleName: sessionRoleName ?? prev.roleName ?? null,
        roleId: sessionRoleId ?? prev.roleId ?? null,
      }));
      if (shouldUpdateRole) {
        applyRoleSnapshot(sessionRoleName || null, sessionRoleId ?? null);
      }

      if (sessionIsGlobalAdmin && !resolvedCompanyId) {
        setAttendantData({
          id: session?.attendant_id ?? null,
          name: session?.name ?? '',
          email: session?.email ?? '',
          company_id: null,
          role_id: sessionRoleId,
          role: sessionRoleName ? { id: sessionRoleId, name: sessionRoleName } : undefined,
          permission_ids: session?.permission_ids || [],
          permissions: session?.permissions || [],
        });
        setIsSuperAdmin(sessionIsSuperAdmin);
        setIsAgent(sessionIsAgent);
        setIsGlobalAdmin(true);
        setIsAdmin(true);
        return session ?? null;
      }

      if (!resolvedCompanyId) {
        setAttendantData({
          id: session?.attendant_id ?? null,
          name: session?.name ?? '',
          email: session?.email ?? '',
          company_id: null,
          role_id: sessionRoleId,
          role: sessionRoleName ? { id: sessionRoleId, name: sessionRoleName } : undefined,
          permission_ids: session?.permission_ids || [],
          permissions: session?.permissions || [],
        });
        setIsSuperAdmin(false);
        setIsAgent(sessionIsAgent);
        setIsGlobalAdmin(sessionIsGlobalAdmin);
        setIsAdmin(sessionIsAdmin);
        return session ?? null;
      }

      const me = await getAttendantMe();
      await applyAttendantData(me);
      return session ?? null;
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      if (status === 401 || status === 403) {
        performLogout();
        setAuthState({ attendantId: null, companyId: null, roleName: null, roleId: null });
        setAttendantData(null);
        setIsSuperAdmin(false);
        setIsAgent(false);
        setIsGlobalAdmin(false);
        setIsAdmin(false);
        applyRoleSnapshot(null, null);
      }
      return null;
    }
  }, [applyAttendantData, applyRoleSnapshot]);

  useEffect(() => {
    if (!isAuthReady) return;
    const storedScope = readStoredAdminScope(normalizedCompanyId);
    const nextScope = isAdmin ? storedScope : 'attendant';
    setAdminConversationScopeState(nextScope);
  }, [normalizedCompanyId, isAdmin, isAuthReady]);

  // Mantém o escopo admin sincronizado entre abas.
  useEffect(() => {
    const handleStorageScope = (event) => {
      const expectedKey = getAdminScopeStorageKey(normalizedCompanyId);
      const isLegacyKey = event.key === ADMIN_SCOPE_STORAGE_KEY && expectedKey !== ADMIN_SCOPE_STORAGE_KEY;
      const isCurrentKey = event.key === expectedKey;
      if (!isCurrentKey && !isLegacyKey) return;

      const nextScope = normalizeAdminScope(event.newValue);
      setAdminConversationScopeState(isAdmin ? nextScope : 'attendant');
    };
    window.addEventListener('storage', handleStorageScope);
    return () => window.removeEventListener('storage', handleStorageScope);
  }, [normalizedCompanyId, isAdmin]);

  const setAdminConversationScope = useCallback(
    (nextScope) => {
      const normalized = normalizeAdminScope(nextScope);
      const safeScope = isAdmin ? normalized : 'attendant';
      setAdminConversationScopeState(safeScope);
      const storageKey = getAdminScopeStorageKey(normalizedCompanyId);
      try {
        window.localStorage.setItem(storageKey, safeScope);
      } catch {
        // ignore persistence failures
      }
    },
    [isAdmin, normalizedCompanyId]
  );

  const setChatSortMode = useCallback((nextSortMode) => {
    const safeSortMode = normalizeConversationSortMode(nextSortMode);
    setChatSortModeState(safeSortMode);
    try {
      window.localStorage.setItem(CHAT_SORT_MODE_STORAGE_KEY, safeSortMode);
    } catch {
      // ignore persistence failures
    }
  }, []);

  const isPageVisible = useCallback(() => {
    if (typeof document === 'undefined') return false;
    const visibilityOk =
      typeof document.visibilityState === 'string' ? document.visibilityState === 'visible' : true;
    const focusOk =
      typeof document.hasFocus === 'function' ? document.hasFocus() : true;
    return visibilityOk && focusOk;
  }, []);

  internalChatWsRuntimeRef.current = {
    emitPopupNotification,
    isPageVisible,
    normalizedAttendantId,
    normalizedCompanyId,
    pathname,
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePageHide = () => {
      internalChatWsPageLifecycleRef.current = 'pagehide';
    };
    const handlePageShow = () => {
      internalChatWsPageLifecycleRef.current = null;
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    writeLocalJsonMap(CHAT_LS_LAST_OPEN_KEY, lastOpenMap);
  }, [lastOpenMap]);

  const markConversationRead = useCallback((conversationOrId, openedAtValue = Date.now()) => {
    const chatId = normalizeConversationId(
      typeof conversationOrId === 'object'
        ? conversationOrId?.id ??
            conversationOrId?.conversation_id ??
            conversationOrId?.conversationId ??
            conversationOrId?.slot_id ??
            conversationOrId?.slotId
        : conversationOrId
    );
    if (!chatId) return 0;

    const openedAt = Number(openedAtValue) || Date.now();
    setIncomingUnreadMap((prev) => {
      if (!prev?.[chatId]) return prev;
      const next = { ...(prev || {}) };
      delete next[chatId];
      return next;
    });
    setLastOpenMap((prev) => {
      const current = Number(prev?.[chatId] || 0);
      if (current >= openedAt) return prev;
      const next = { ...(prev || {}), [chatId]: openedAt };
      setTimeout(() => {
        try {
          window.dispatchEvent(new Event('storage'));
        } catch {
          // Evento local auxiliar; falha nao deve quebrar o contador.
        }
      }, 0);
      return next;
    });
    return openedAt;
  }, []);

  const registerIncomingUnread = useCallback(
    ({ chatId: rawChatId, messageKey, occurredAt } = {}) => {
      const chatId = normalizeConversationId(rawChatId);
      if (!chatId || !messageKey) return false;

      if (String(activeChatIdRef.current ?? '') === chatId && isPageVisible()) {
        markConversationRead(chatId, Date.now());
        return false;
      }

      const now = Date.now();
      const dedupeKey = `${chatId}:${String(messageKey)}`;
      for (const [key, ts] of unreadDedupeRef.current.entries()) {
        if (now - Number(ts || 0) > UNREAD_DEDUP_TTL_MS) {
          unreadDedupeRef.current.delete(key);
        }
      }
      if (unreadDedupeRef.current.has(dedupeKey)) {
        return false;
      }
      unreadDedupeRef.current.set(dedupeKey, now);
      if (unreadDedupeRef.current.size > MAX_UNREAD_DEDUP_ENTRIES) {
        const staleKeys = Array.from(unreadDedupeRef.current.entries())
          .sort((left, right) => left[1] - right[1])
          .slice(0, unreadDedupeRef.current.size - MAX_UNREAD_DEDUP_ENTRIES)
          .map(([key]) => key);
        staleKeys.forEach((key) => unreadDedupeRef.current.delete(key));
      }

      const eventTs = Number(occurredAt || now) || now;
      setIncomingUnreadMap((prev) => {
        const current = prev?.[chatId] || null;
        if (current?.lastMessageKey === messageKey) return prev;

        const next = {
          ...(prev || {}),
          [chatId]: {
            count: Math.max(1, Number(current?.count || 0) + 1),
            lastMessageKey: String(messageKey),
            lastOccurredAt: eventTs,
          },
        };

        const keys = Object.keys(next);
        if (keys.length > MAX_INCOMING_UNREAD_ENTRIES) {
          keys
            .sort((leftKey, rightKey) => {
              const leftTs = Number(next[leftKey]?.lastOccurredAt || 0);
              const rightTs = Number(next[rightKey]?.lastOccurredAt || 0);
              return leftTs - rightTs;
            })
            .slice(0, keys.length - MAX_INCOMING_UNREAD_ENTRIES)
            .forEach((key) => {
              delete next[key];
            });
        }

        return next;
      });
      return true;
    },
    [isPageVisible, markConversationRead]
  );

  const getConversationUnreadCount = useCallback(
    (conversation) =>
      resolveConversationUnreadCount(conversation, {
        lastOpenMap,
        incomingUnreadMap,
        isHiddenStatus: (value) => {
          const status = String(value ?? '').toLowerCase();
          return (
            status.includes('waiting_evaluation') ||
            status.includes('workflow_waiting_input') ||
            status.includes('workflow_handling')
          );
        },
      }),
    [incomingUnreadMap, lastOpenMap]
  );

  const handleConversationRealtimeUnread = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') return;
      const eventType = normalizeConversationEventType(payload);

      const slotPayload = getWsSlotPayload(payload);
      const payloadMessage = extractPayloadMessage(payload, slotPayload);
      const chatId = getWsSlotPayloadId(slotPayload, payloadMessage, payload);
      const isExplicitNewMessage = payload?.is_new_message === true || eventType === 'new_message';
      const hasMessageEvidence = Boolean(
        payloadMessage && typeof payloadMessage === 'object'
      );
      const debugUnreadReturn = () => {
        if (!isNotificationDebugEnabled()) return;
        console.debug('[BLUEZAP_UNREAD_DEBUG]', {
          eventType,
          chatId,
          isExplicitNewMessage,
          hasMessageEvidence,
          messageKey: resolveIncomingPayloadMessageKey(
            payload,
            slotPayload,
            payloadMessage
          ),
          payloadKeys: Object.keys(payload || {}),
          slotPayloadKeys: Object.keys(slotPayload || {}),
        });
      };

      if (eventType.startsWith('group_')) {
        debugUnreadReturn();
        return;
      }
      if (chatId === null || chatId === undefined) {
        debugUnreadReturn();
        return;
      }
      if (isEvaluationPayload(slotPayload, payloadMessage)) {
        debugUnreadReturn();
        return;
      }

      if (!isExplicitNewMessage && !hasMessageEvidence) {
        debugUnreadReturn();
        return;
      }

      const isStatusEvent = isStatusOnlyMessage(payloadMessage) || (
        !isExplicitNewMessage &&
        ['message_update', 'message_updated', 'message_status', 'message_status_update', 'message_status_updated'].includes(eventType)
      );
      if (isStatusEvent) {
        debugUnreadReturn();
        return;
      }

      if (hasMessageEvidence && !isClientMessage(payloadMessage)) {
        debugUnreadReturn();
        return;
      }
      if (!hasMessageEvidence && !isExplicitNewMessage) {
        debugUnreadReturn();
        return;
      }

      const messageKey = resolveIncomingPayloadMessageKey(payload, slotPayload, payloadMessage);
      if (!messageKey) {
        debugUnreadReturn();
        if (String(activeChatIdRef.current ?? '') === String(normalizeConversationId(chatId)) && isPageVisible()) {
          markConversationRead(chatId, Date.now());
        }
        return;
      }

      const occurredAt =
        getMessageActivityTimestamp(payloadMessage) ||
        getMessageActivityTimestamp(slotPayload?.messages?.[slotPayload.messages.length - 1]) ||
        Date.now();
      registerIncomingUnread({ chatId, messageKey, occurredAt });
    },
    [isPageVisible, markConversationRead, registerIncomingUnread]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleIncomingUnread = (event) => {
      registerIncomingUnread(event?.detail || {});
    };
    window.addEventListener('bluezap:incoming-message-unread', handleIncomingUnread);
    return () => {
      window.removeEventListener('bluezap:incoming-message-unread', handleIncomingUnread);
    };
  }, [registerIncomingUnread]);

  const handleInternalChatPresenceEvent = useCallback((payload) => {
    if (!payload?.attendant_id) return;
    setInternalChatPresence((prev) => ({
      ...prev,
      [payload.attendant_id]: !!payload.is_online,
    }));
  }, []);

  const normalizePresenceKey = useCallback((value) => {
    if (value === null || value === undefined) return '';
    const raw = String(value).trim().toLowerCase();
    return raw;
  }, []);

  const coercePresenceExpiryMs = useCallback((payload) => {
    const rawExpiry = payload?.expires_at ?? payload?.expiresAt ?? null;
    if (rawExpiry) {
      const parsed = Date.parse(rawExpiry);
      if (!Number.isNaN(parsed)) return parsed;
    }
    const ttlSecondsRaw = Number(payload?.ttl_seconds ?? payload?.ttlSeconds ?? 0);
    const ttlSeconds = Number.isFinite(ttlSecondsRaw) && ttlSecondsRaw > 0 ? ttlSecondsRaw : 20;
    return Date.now() + ttlSeconds * 1000;
  }, []);

  const handleWhatsappPresenceEvent = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') return;
      const payloadCompanyId = normalizeOptionalNumber(payload.company_id ?? payload.companyId);
      if (payloadCompanyId !== null && normalizedCompanyId !== null && payloadCompanyId !== normalizedCompanyId) {
        return;
      }

      const chatKey = normalizePresenceKey(
        payload.chat_jid ?? payload.chatJid ?? payload.chat ?? payload.jid
      );
      if (!chatKey) return;

      const nextStatus = String(payload.status || '').trim().toLowerCase();
      const expiresAtMs = coercePresenceExpiryMs(payload);
      const lastSeenAt = payload.last_seen_at ?? payload.lastSeenAt ?? null;

      setWhatsappPresence((prev) => ({
        ...prev,
        [chatKey]: {
          chatJid: chatKey,
          participantJid: normalizePresenceKey(
            payload.participant_jid ?? payload.participantJid ?? payload.participant
          ),
          status: nextStatus,
          lastSeenAt,
          isFromMe: Boolean(payload.is_from_me ?? payload.isFromMe),
          expiresAt: expiresAtMs,
          updatedAt: Date.now(),
        },
      }));
    },
    [coercePresenceExpiryMs, normalizePresenceKey, normalizedCompanyId]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setWhatsappPresence((prev) => {
        const entries = Object.entries(prev);
        if (!entries.length) return prev;
        let changed = false;
        const next = {};
        entries.forEach(([key, value]) => {
          if (!value || typeof value !== 'object') {
            changed = true;
            return;
          }
          if (typeof value.expiresAt === 'number' && value.expiresAt <= now) {
            changed = true;
            return;
          }
          next[key] = value;
        });
        return changed ? next : prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const setAttendantWsHandler = useCallback((handler) => {
    attendantWsHandlerRef.current = typeof handler === 'function' ? handler : null;
  }, []);

  const clearInternalChatAttendanceMention = useCallback((chatId) => {
    const normalizedChatId = normalizeOptionalNumber(chatId);
    if (normalizedChatId === null) return;
    const key = String(normalizedChatId);
    setInternalChatAttendanceMentions((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, key)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleAttendantWsMessage = useCallback((message) => {
    // Avoid double-processing when /ws/conversations is active; attendant WS is for the active chat.
    if (!chatClientsEnabled && typeof applyIncomingPayloadRef.current === 'function') {
      applyIncomingPayloadRef.current(message, { fallbackToRefetch: true });
    }
    const handler = attendantWsHandlerRef.current;
    if (typeof handler === 'function') {
      handler(message, {
        listsManagedByChatClients: chatClientsEnabled,
        source: 'attendant_ws',
      });
    }
  }, [chatClientsEnabled]);

  useAttendantWebSocket(
    normalizedAttendantId,
    normalizedCompanyId,
    handleAttendantWsMessage,
    setIsAttendantWsConnected
  );

  const syncInternalChatUnreadFromServer = useCallback(
    async (reason = 'manual') => {
      if (
        !isAuthReady ||
        !canAccessInternalChat ||
        normalizedCompanyId === null ||
        normalizedAttendantId === null
      ) {
        return null;
      }

      const syncKey = `${normalizedCompanyId}:${normalizedAttendantId}`;
      const currentRequest = internalChatUnreadSyncInFlightRef.current;
      if (currentRequest?.key === syncKey) {
        return currentRequest.promise;
      }

      const request = getInternalChatUnreadSummary();
      internalChatUnreadSyncInFlightRef.current = { key: syncKey, promise: request };
      try {
        const summary = await request;
        const unreadMap =
          summary?.unread_map && typeof summary.unread_map === 'object'
            ? summary.unread_map
            : {};
        replaceInternalChatUnreadMap(
          normalizedCompanyId,
          normalizedAttendantId,
          unreadMap,
          { source: `chat-context:server-sync:${reason}` }
        );
        return summary;
      } catch (err) {
        const status = Number(err?.status ?? err?.response?.status);
        if (status !== 401 && status !== 403) {
          logSafeWarning('Falha ao sincronizar mensagens internas não lidas.', err);
        }
        return null;
      } finally {
        if (internalChatUnreadSyncInFlightRef.current?.promise === request) {
          internalChatUnreadSyncInFlightRef.current = null;
        }
      }
    },
    [
      canAccessInternalChat,
      isAuthReady,
      normalizedAttendantId,
      normalizedCompanyId,
    ]
  );

  const handleInternalChatMessageEvent = useCallback((payload) => {
    if (!payload?.message) return;
    const messageText = (payload.message.content || '').trim();
    const threadId =
      normalizeOptionalNumber(payload?.thread?.id) ??
      normalizeOptionalNumber(payload?.message?.thread_id) ??
      normalizeOptionalNumber(payload?.message?.threadId) ??
      null;
    const messageId =
      payload?.message?.id ??
      payload?.message?.message_id ??
      payload?.message?.messageId ??
      null;
    const {
      emitPopupNotification: currentEmitPopupNotification,
      isPageVisible: currentIsPageVisible,
      normalizedAttendantId: currentNormalizedAttendantId,
      normalizedCompanyId: currentNormalizedCompanyId,
      pathname: currentPathname,
    } = internalChatWsRuntimeRef.current || {};
    const pageVisible =
      typeof currentIsPageVisible === 'function' ? currentIsPageVisible() : false;
    const isInternalChatRouteVisible =
      String(currentPathname || '').startsWith('/internal-chat') && pageVisible;
    const attendanceMention = parseAttendanceMentionFromInternalMessage(messageText);
    const hasAttendanceMention =
      attendanceMention?.chatId !== null && attendanceMention?.chatId !== undefined;
    const transferRequest = parseTransferRequestFromInternalMessage(messageText);
    const hasTransferRequest = transferRequest?.chatId !== null && transferRequest?.chatId !== undefined;
    const {
      allowPopupNotification,
      allowSoundNotification,
      attendantId: currentAttendantId,
      isAdmin: currentIsAdmin,
      adminScope: currentAdminScope,
    } = internalChatNotificationPrefsRef.current;

    const senderId = normalizeOptionalNumber(payload.message.sender_id);
    const myId = normalizeOptionalNumber(currentAttendantId);
    debugNotificationFlow('internal_chat_handler_entered', {
      thread_id: threadId,
      message_id: messageId,
      sender_id: senderId,
      current_attendant_id: myId,
    });
    debugNotificationFlow('internal_chat_route_state', {
      pathname: currentPathname || '',
      page_visible: pageVisible,
      is_internal_chat_route_visible: isInternalChatRouteVisible,
    });
    debugNotificationFlow('internal_chat_notification_preferences', {
      allow_popup_notification: Boolean(allowPopupNotification),
      allow_sound_notification: Boolean(allowSoundNotification),
      has_transfer_request: Boolean(hasTransferRequest),
      has_attendance_mention: Boolean(hasAttendanceMention),
      is_admin: Boolean(currentIsAdmin),
      admin_scope: currentAdminScope || null,
    });
    if (myId !== null && senderId !== null && myId === senderId) {
      debugNotificationFlow('internal_chat_rejected', {
        reason: 'own_message',
        thread_id: threadId,
        message_id: messageId,
      });
      return;
    }

    const participant = payload.thread?.participant || null;
    const targetAttendantId = participant?.id ?? (senderId !== null ? senderId : null);
    const isAdminObserverThread = payload?.thread?.can_reply === false;
    const shouldIgnoreByAdminScope =
      Boolean(currentIsAdmin) &&
      currentAdminScope !== 'all' &&
      isAdminObserverThread;
    if (shouldIgnoreByAdminScope) {
      debugNotificationFlow('internal_chat_rejected', {
        reason: 'admin_scope',
        thread_id: threadId,
        message_id: messageId,
      });
      return;
    }
    const senderName =
      payload.message.sender_name ||
      participant?.name ||
      (senderId !== null ? `Atendente #${senderId}` : 'Mensagem do chat interno');
    const body = messageText || 'Nova mensagem no chat interno.';

    if (
      !isInternalChatRouteVisible &&
      currentNormalizedCompanyId !== null &&
      currentNormalizedAttendantId !== null &&
      currentNormalizedAttendantId !== undefined &&
      threadId !== null
    ) {
      incrementInternalChatUnreadThread(
        currentNormalizedCompanyId,
        currentNormalizedAttendantId,
        threadId,
        1,
        { source: 'chat-context' }
      );
    }

    if (
      !allowPopupNotification &&
      !allowSoundNotification &&
      !hasTransferRequest &&
      !hasAttendanceMention
    ) {
      debugNotificationFlow('internal_chat_rejected', {
        reason: 'notifications_disabled',
        thread_id: threadId,
        message_id: messageId,
      });
      return;
    }

    if (hasAttendanceMention) {
      const chatIdKey = String(attendanceMention.chatId);
      const messageIdKey =
        payload?.message?.id ??
        payload?.message?.message_id ??
        payload?.message?.messageId ??
        null;
      const createdAtValue =
        payload?.message?.created_at ??
        payload?.message?.createdAt ??
        payload?.message?.timestamp ??
        null;
      const currentSignature = [
        messageIdKey ?? '',
        createdAtValue ?? '',
        senderId ?? '',
        body,
      ].join('|');
      const nowTs = Date.now();
      setInternalChatAttendanceMentions((prev) => {
        const next = { ...(prev || {}) };
        const previousEntry =
          next[chatIdKey] && typeof next[chatIdKey] === 'object'
            ? next[chatIdKey]
            : null;
        const previousCount = Number(previousEntry?.unreadCount || 0);
        const isDuplicate = previousEntry?.lastMessageSignature === currentSignature;
        const unreadCount = isDuplicate
          ? Math.max(1, previousCount)
          : Math.max(1, previousCount + 1);
        next[chatIdKey] = {
          chatId: attendanceMention.chatId,
          threadId,
          senderId,
          senderName,
          targetAttendantId,
          preview: body,
          createdAt: createdAtValue,
          receivedAt: nowTs,
          messageId: messageIdKey,
          unreadCount,
          lastMessageSignature: currentSignature,
        };

        const keys = Object.keys(next);
        if (keys.length > MAX_INTERNAL_ATTENDANCE_MENTIONS) {
          keys
            .sort((leftKey, rightKey) => {
              const leftTs = Number(next[leftKey]?.receivedAt || 0);
              const rightTs = Number(next[rightKey]?.receivedAt || 0);
              return leftTs - rightTs;
            })
            .slice(0, keys.length - MAX_INTERNAL_ATTENDANCE_MENTIONS)
            .forEach((staleKey) => {
              delete next[staleKey];
            });
        }

        return next;
      });
    }

    const title = hasTransferRequest
      ? senderName
        ? `Solicitação de transferência · ${senderName}`
        : 'Solicitação de transferência'
      : senderName
        ? `Chat Interno · ${senderName}`
        : 'Chat Interno';
    const avatarUrl = resolveInternalChatAvatarUrl(participant) || '/logo.png';

    const focusInternalChat = (targetId) => {
      try {
        window.focus?.();
      } catch {
        // Foco da janela pode ser bloqueado pelo navegador.
      }
      openInternalChat({ targetAttendantId: targetId });
    };

    const focusCustomerChat = (chatId) => {
      try {
        window.focus?.();
      } catch {
        // Foco da janela pode ser bloqueado pelo navegador.
      }

      const hasChatId = chatId !== null && chatId !== undefined;
      if (window.location.pathname !== '/chatpage') {
        if (hasChatId) {
          try {
            sessionStorage.setItem('bluezap:pendingChatFocus', String(chatId));
          } catch {
            // Falha em sessionStorage nao deve impedir o redirecionamento.
          }
        }
        window.location.assign('/chatpage');
        return;
      }

      if (hasChatId) {
        window.dispatchEvent(
          new CustomEvent('bluezap:focus-chat', {
            detail: { chatId },
          })
        );
      }
    };

    const notificationTag = threadId !== null ? `internal-chat-thread-${threadId}` : 'internal-chat';
    const shouldRequestPopup =
      !isInternalChatRouteVisible && (allowPopupNotification || hasTransferRequest);

    if (shouldRequestPopup && typeof currentEmitPopupNotification === 'function') {
      debugNotificationFlow('internal_chat_popup_requested', {
        thread_id: threadId,
        message_id: messageId,
        notification_tag: notificationTag,
        is_internal_chat_route_visible: isInternalChatRouteVisible,
        allow_popup_notification: Boolean(allowPopupNotification),
      });
      currentEmitPopupNotification(title, {
        body,
        icon: avatarUrl,
        tag: notificationTag,
        systemWhenVisible: true,
        debugMeta: {
          chat_id: threadId,
          message_key: threadId !== null && messageId !== null
            ? `internal-chat:${threadId}:${messageId}`
            : messageId !== null
              ? `internal-chat:${messageId}`
              : null,
          kind: hasTransferRequest ? 'transfer-request' : 'internal-message',
        },
        attention: {
          enabled: true,
          kind: hasTransferRequest ? 'transfer-request' : 'internal-message',
          title: senderName || title,
        },
        onClick: (meta) => {
          if (hasTransferRequest) {
            trackAnalyticsEvent('notification_open_transfer_request', {
              source: meta?.source || 'unknown',
              company_id: currentNormalizedCompanyId,
              attendant_id: currentNormalizedAttendantId,
              chat_id: transferRequest?.chatId ?? null,
              sender_attendant_id: senderId ?? null,
            });
            focusCustomerChat(transferRequest?.chatId ?? null);
            return;
          }

          trackAnalyticsEvent('notification_open_internal_chat', {
            source: meta?.source || 'unknown',
            company_id: currentNormalizedCompanyId,
            attendant_id: currentNormalizedAttendantId,
            target_attendant_id: targetAttendantId ?? null,
          });
          focusInternalChat(targetAttendantId);
        },
      });
    } else if (shouldRequestPopup) {
      debugNotificationFlow('internal_chat_popup_unavailable', {
        typeof_emit_popup_notification: typeof currentEmitPopupNotification,
      });
    }

    if (!isInternalChatRouteVisible && allowSoundNotification) {
      debugNotificationFlow('internal_chat_sound_requested', {
        thread_id: threadId,
        message_id: messageId,
      });
      playInternalChatMessageNotificationSound();
    }
  }, []);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !isAuthReady ||
      !canAccessInternalChat ||
      normalizedCompanyId === null ||
      normalizedAttendantId === null
    ) {
      return undefined;
    }

    const syncUnread = (reason) => {
      syncInternalChatUnreadFromServer(reason);
    };
    const handleFocus = () => syncUnread('window-focus');
    const handleOnline = () => syncUnread('browser-online');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncUnread('tab-visible');
      }
    };

    syncUnread('initial');
    const intervalId = window.setInterval(
      () => syncUnread('interval'),
      INTERNAL_CHAT_UNREAD_SYNC_INTERVAL_MS
    );
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    canAccessInternalChat,
    isAuthReady,
    normalizedAttendantId,
    normalizedCompanyId,
    syncInternalChatUnreadFromServer,
  ]);

  useEffect(() => {
    if (!isAuthReady || !normalizedAttendantId || !normalizedCompanyId || !canAccessInternalChat) {
      if (internalChatWsRef.current) {
        const currentSocket = internalChatWsRef.current;
        const currentMeta = internalChatWsMetaRef.current.get(currentSocket);
        if (currentMeta) {
          currentMeta.cleanupReason = 'auth_or_access_disabled';
        }
        try {
          currentSocket.close(1000, 'auth_or_access_disabled');
        } catch {
          // Socket ja pode estar fechado; seguimos limpando a referencia.
        }
        internalChatWsRef.current = null;
      }
      return undefined;
    }

    const clientId = internalChatWsClientIdRef.current;
    const wsMetaMap = internalChatWsMetaRef.current;
    const wsUrl =
      `${WS_BASE_URL}/v1/${normalizedCompanyId}/internal-chat/ws` +
      `?attendant_id=${encodeURIComponent(normalizedAttendantId)}` +
      `&client_id=${encodeURIComponent(clientId)}`;
    let reconnectTimeout = null;
    let disposed = false;

    const clearReconnectTimeout = () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimeout) return;
      const reconnectDelay = applyReconnectJitter(INTERNAL_CHAT_RECONNECT_BASE_DELAY_MS);
      logInternalChatWs('info', 'Reconexao agendada.', {
        company_id: normalizedCompanyId,
        attendant_id: normalizedAttendantId,
        client_id: clientId,
        reconnect_delay_ms: reconnectDelay,
        ...getInternalChatWsEnvironment(),
      });
      reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        connect();
      }, reconnectDelay);
    };

    const connect = async () => {
      clearReconnectTimeout();
      if (disposed) return;
      const currentSocket = internalChatWsRef.current;
      if (
        currentSocket &&
        (currentSocket.readyState === WebSocket.OPEN ||
          currentSocket.readyState === WebSocket.CONNECTING)
      ) {
        logInternalChatWs('info', 'Conexao reutilizada; nova tentativa ignorada.', {
          company_id: normalizedCompanyId,
          attendant_id: normalizedAttendantId,
          client_id: clientId,
          ready_state: currentSocket.readyState,
          ...getInternalChatWsEnvironment(),
        });
        return;
      }
      const health = await ensureSessionHealthy();
      if (disposed) return;
      if (!health?.allowed) {
        if (isSessionHealthAuthFailure(health)) {
          logInternalChatWs('warning', 'Conexao abortada por sessao invalida.', {
            company_id: normalizedCompanyId,
            attendant_id: normalizedAttendantId,
            client_id: clientId,
            ...getInternalChatWsEnvironment(),
          });
          return;
        }
        scheduleReconnect();
        return;
      }

      const ws = new WebSocket(wsUrl);
      internalChatWsRef.current = ws;
      const socketInstanceId = `internal-chat-ws-${++internalChatWsInstanceSeqRef.current}`;
      const connectionAttempt = ++internalChatWsAttemptRef.current;
      const openedAtMs = Date.now();
      const meta = {
        socket_instance_id: socketInstanceId,
        company_id: normalizedCompanyId,
        attendant_id: normalizedAttendantId,
        client_id: clientId,
        connection_attempt: connectionAttempt,
        opened_at: new Date(openedAtMs).toISOString(),
        opened_at_ms: openedAtMs,
        cleanupReason: null,
        errored: false,
      };
      wsMetaMap.set(ws, meta);
      let opened = false;

      logInternalChatWs('info', 'Conectando.', {
        socket_instance_id: socketInstanceId,
        company_id: normalizedCompanyId,
        attendant_id: normalizedAttendantId,
        client_id: clientId,
        connection_attempt: connectionAttempt,
        ...getInternalChatWsEnvironment(),
      });

      ws.onopen = () => {
        opened = true;
        meta.opened_at_ms = Date.now();
        meta.opened_at = new Date(meta.opened_at_ms).toISOString();
        internalChatWsFailedAttemptsRef.current = 0;
        logInternalChatWs('info', 'Aberto.', {
          socket_instance_id: socketInstanceId,
          company_id: normalizedCompanyId,
          attendant_id: normalizedAttendantId,
          client_id: clientId,
          connection_attempt: connectionAttempt,
          opened_at: meta.opened_at,
          ...getInternalChatWsEnvironment(),
        });
        dispatchInternalChatEvent({
          type: 'sync_requested',
          reason: 'websocket_open',
        });
        syncInternalChatUnreadFromServer('websocket-open');
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'presence') {
            handleInternalChatPresenceEvent(payload);
          } else if (payload.type === 'thread_created') {
            dispatchInternalChatEvent(payload);
          } else if (payload.type === 'message') {
            debugNotificationFlow('internal_chat_ws_message_received', {
              thread_id:
                normalizeOptionalNumber(payload?.thread?.id) ??
                normalizeOptionalNumber(payload?.message?.thread_id) ??
                normalizeOptionalNumber(payload?.message?.threadId) ??
                null,
              message_id:
                payload?.message?.id ??
                payload?.message?.message_id ??
                payload?.message?.messageId ??
                null,
              sender_id: normalizeOptionalNumber(payload?.message?.sender_id),
              current_attendant_id: normalizedAttendantId,
              payload_type: payload.type,
            });
            handleInternalChatMessageEvent(payload);
            dispatchInternalChatEvent(payload);
          }
        } catch (err) {
          console.error('Erro ao processar mensagem do chat interno.', getSafeErrorDetails(err));
        }
      };

      ws.onerror = () => {
        meta.errored = true;
        logInternalChatWs('error', 'Erro de conexao.', {
          socket_instance_id: socketInstanceId,
          company_id: normalizedCompanyId,
          attendant_id: normalizedAttendantId,
          client_id: clientId,
          connection_attempt: connectionAttempt,
          opened_at: meta.opened_at,
          lifetime_ms: Date.now() - meta.opened_at_ms,
          ...getInternalChatWsEnvironment(),
        });
      };

      ws.onclose = (event) => {
        const pageLifecycleReason = internalChatWsPageLifecycleRef.current;
        const cleanupReason = meta.cleanupReason || pageLifecycleReason || null;
        const lifetimeMs = Date.now() - meta.opened_at_ms;
        const closeDetails = {
          socket_instance_id: socketInstanceId,
          company_id: normalizedCompanyId,
          attendant_id: normalizedAttendantId,
          client_id: clientId,
          connection_attempt: connectionAttempt,
          opened_at: meta.opened_at,
          lifetime_ms: lifetimeMs,
          close_code: event?.code ?? null,
          close_reason: event?.reason || '',
          was_clean: Boolean(event?.wasClean),
          cleanup_reason: cleanupReason,
          page_url: getInternalChatWsEnvironment().page_url,
          visibility_state: getInternalChatWsEnvironment().visibility_state,
          online_state: getInternalChatWsEnvironment().online_state,
        };
        const expectedClose = isExpectedInternalChatWsClose(event, cleanupReason);
        logInternalChatWs(
          expectedClose ? 'info' : 'warning',
          expectedClose ? 'Fechado por cleanup esperado.' : 'Fechado inesperadamente.',
          closeDetails
        );
        if (internalChatWsRef.current === ws) {
          internalChatWsRef.current = null;
          if (disposed || expectedClose || isInternalChatAuthCloseEvent(event)) {
            return;
          }
          if (!opened) {
            const nextFailures = internalChatWsFailedAttemptsRef.current + 1;
            internalChatWsFailedAttemptsRef.current = nextFailures;
          } else {
            internalChatWsFailedAttemptsRef.current = 0;
          }
          scheduleReconnect();
        }
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnectTimeout();
      internalChatWsFailedAttemptsRef.current = 0;
      if (internalChatWsRef.current) {
        const currentSocket = internalChatWsRef.current;
        const currentMeta = wsMetaMap.get(currentSocket);
        if (currentMeta) {
          currentMeta.cleanupReason = 'effect_cleanup';
        }
        currentSocket.close(1000, 'effect_cleanup');
      }
    };
  }, [
    canAccessInternalChat,
    handleInternalChatMessageEvent,
    handleInternalChatPresenceEvent,
    isAuthReady,
    normalizedAttendantId,
    normalizedCompanyId,
    syncInternalChatUnreadFromServer,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncAuthState = () => {
      setAuthState((prev) => {
        const next = readAuthSnapshot();
        if (
          prev.attendantId === next.attendantId &&
          prev.companyId === next.companyId &&
          prev.token === next.token &&
          prev.roleName === next.roleName &&
          prev.roleId === next.roleId
        ) {
          return prev;
        }
        return next;
      });
    };

    window.addEventListener('storage', syncAuthState);
    window.addEventListener('bluezap:auth-changed', syncAuthState);
    window.addEventListener(LOGOUT_EVENT, syncAuthState);

    return () => {
      window.removeEventListener('storage', syncAuthState);
      window.removeEventListener('bluezap:auth-changed', syncAuthState);
      window.removeEventListener(LOGOUT_EVENT, syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleSessionHealth = () => {
      setSessionHealth(getSessionHealthSnapshot());
    };

    window.addEventListener(SESSION_HEALTH_EVENT, handleSessionHealth);
    return () => {
      window.removeEventListener(SESSION_HEALTH_EVENT, handleSessionHealth);
    };
  }, []);

  useEffect(() => {
    if (!attendantData) {
      return;
    }

    const nextAttendantId = normalizeOptionalNumber(attendantData?.id);
    const nextCompanyId = normalizeOptionalNumber(
      attendantData?.company_id ?? authState.companyId
    );
    const currentCompanyId = normalizeOptionalNumber(authState.companyId);

    if (nextAttendantId === null && nextCompanyId === currentCompanyId) {
      return;
    }

    const attendantRoleId =
      attendantData?.role_id ??
      attendantData?.role?.id ??
      null;
    const attendantRoleName =
      attendantData?.role?.name ??
      attendantData?.role_name ??
      null;

    if (attendantRoleId !== null || (attendantRoleName && typeof attendantRoleName === 'string')) {
      applyRoleSnapshot(attendantRoleName || null, attendantRoleId);
    }

    setAuthState((prev) => {
      const prevAttendantId = normalizeOptionalNumber(prev.attendantId);
      const prevCompanyId = normalizeOptionalNumber(prev.companyId);
      const updatedAttendantId = nextAttendantId ?? prevAttendantId;
      const updatedCompanyId = nextCompanyId ?? prevCompanyId;

      if (prevAttendantId === updatedAttendantId && prevCompanyId === updatedCompanyId) {
        return prev;
      }

      return {
        ...prev,
        attendantId: updatedAttendantId,
        companyId: updatedCompanyId,
      };
    });
  }, [attendantData, authState.companyId, applyRoleSnapshot]);

  const {
    waitingList,
    attendingList,
    allList,
    clientContacts,
    loading,
    scopeLoading,
    error,
    refetch,
    fetchClientContacts,
    setAllList,
    applyIncomingPayload,
  } = useChatClients(normalizedAttendantId, {
    isAdmin,
    scope: conversationScope,
    companyId: normalizedCompanyId,
    enabled: chatClientsEnabled,
    realtimeEnabled: chatRealtimeEnabled,
    hasAllTeamConversationAccess,
    attendantTeamIds,
    onConversationEvent: handleConversationRealtimeUnread,
    sortMode: chatSortMode,
  });
  applyIncomingPayloadRef.current = applyIncomingPayload;

  const requestCompanySettings = useCallback(
    async (targetCompanyId, options = {}) => {
      const companyId = targetCompanyId ?? effectiveCompanyId;
      if (!companyId) {
        return null;
      }
      try {
        return await getCompanyById(companyId, options);
      } catch (err) {
        logSafeError('Erro ao buscar configurações da empresa.', err);
        return null;
      }
    },
    [effectiveCompanyId]
  );

  const refreshCompanySettings = useCallback(
    async (targetCompanyId, options = {}) => {
      const settings = await requestCompanySettings(targetCompanyId, options);
      setCompanySettings(settings);
      return settings;
    },
    [requestCompanySettings]
  );

  useEffect(() => {
    let cancelled = false;

    const attendantIdValue = normalizeOptionalNumber(authState.attendantId);
    const companyIdValue = normalizeOptionalNumber(authState.companyId);

    const prev = prevAuthRef.current;
    const authChanged =
      prev.attendantId !== attendantIdValue || prev.companyId !== companyIdValue;
    if (authChanged) {
      const shouldDelayRoleResolution = attendantIdValue !== null && companyIdValue === null;
      setAttendantData(null);
      setCompanySettings(null);
      setIsAdmin(false);
      setIsSuperAdmin(shouldDelayRoleResolution ? null : false);
      setIsAgent(false);
      setIsGlobalAdmin(false);
      prevAuthRef.current = { attendantId: attendantIdValue, companyId: companyIdValue };
    }
    if (!authChanged) {
      prevAuthRef.current = { attendantId: attendantIdValue, companyId: companyIdValue };
    }

    if (attendantIdValue === null || companyIdValue === null) {
      setCompanySettings(null);
      return undefined;
    }

    if (bootstrapInFlightRef.current) {
      return undefined;
    }

    const rebootstrap = async () => {
      const me = await refreshAttendantData();
      if (cancelled) return;
      if (companyIdValue !== null || me?.company_id) {
        await refreshCompanySettings(companyIdValue ?? me?.company_id ?? null);
      } else {
        setCompanySettings(null);
      }
    };

    rebootstrap();

    return () => {
      cancelled = true;
    };
  }, [authState.attendantId, authState.companyId, refreshAttendantData, refreshCompanySettings]);

  const handleChatsSynced = useCallback((syncedConversations) => {
    if (!Array.isArray(syncedConversations) || syncedConversations.length === 0) {
      return;
    }

    setAllList(currentList => {
      const existingIds = new Set(currentList.map(c => c.id));
      const newConversations = syncedConversations.filter(
        (c) => !existingIds.has(c.id) && !isConversationAwaitingEvaluation(c)
      );
      
      if (newConversations.length > 0) {
        return [...currentList, ...newConversations];
      }
      
      return currentList;
    });

  }, [setAllList]);

  useEffect(() => {
    let isCancelled = false;

    const hasAuthCookie = hasReadableSessionCookie();
    const skipBootstrapOnPublicAuth = isPublicAuthRoute() && !hasAuthCookie;

    const bootstrapKey = buildBootstrapKey(
      hasAuthCookie,
      authState?.attendantId,
      authState?.companyId
    );

    if (bootstrapKeyRef.current === bootstrapKey) {
      return () => {
        isCancelled = true;
      };
    }

    setIsAuthReady(false);

    if (skipBootstrapOnPublicAuth) {
      setAuthState({ attendantId: null, companyId: null, roleName: null, roleId: null });
      setAttendantData(null);
      setIsSuperAdmin(null);
      setIsAgent(false);
      setIsGlobalAdmin(false);
      setIsAdmin(false);
      applyRoleSnapshot(null, null);
      bootstrapKeyRef.current = bootstrapKey;
      setIsAuthReady(true);
      return () => {
        isCancelled = true;
      };
    }

    async function bootstrapAttendant() {
      bootstrapInFlightRef.current = true;
      try {
        const session = await getSession();
        const storedSession = getAuthSession();
        const resolvedCompanyId =
          session?.company_id === null || session?.company_id === undefined
            ? storedSession?.companyId ?? null
            : session.company_id;

        if (isCancelled) return;

        const sessionRoleName =
          typeof session?.role === 'string' && session.role.trim() ? session.role : null;
        const sessionRoleId = session?.role_id ?? null;
        const shouldUpdateRole = sessionRoleName !== null || sessionRoleId !== null;
        const {
          isAdmin: sessionIsAdmin,
          isSuperAdmin: sessionIsSuperAdmin,
          isAgent: sessionIsAgent,
          isGlobalAdmin: sessionIsGlobalAdmin,
        } =
          resolveAdminPrivileges({}, sessionRoleName, sessionRoleId);

        setAuthSession({
          attendantId: session?.attendant_id,
          companyId: resolvedCompanyId,
          roleName: sessionRoleName,
          roleId: sessionRoleId,
        });
        setAuthState((prev) => ({
          ...prev,
          attendantId: session?.attendant_id ?? null,
          companyId: resolvedCompanyId ?? null,
          roleName: sessionRoleName ?? prev.roleName ?? null,
          roleId: sessionRoleId ?? prev.roleId ?? null,
        }));
        if (shouldUpdateRole) {
          applyRoleSnapshot(sessionRoleName || null, sessionRoleId ?? null);
        }

        if (sessionIsGlobalAdmin && !resolvedCompanyId) {
          setAttendantData({
            id: session?.attendant_id ?? null,
            name: session?.name ?? '',
            email: session?.email ?? '',
            company_id: null,
            role_id: sessionRoleId,
            role: sessionRoleName ? { id: sessionRoleId, name: sessionRoleName } : undefined,
            permission_ids: session?.permission_ids || [],
            permissions: session?.permissions || [],
          });
          setIsSuperAdmin(sessionIsSuperAdmin);
          setIsAgent(sessionIsAgent);
          setIsGlobalAdmin(true);
          setIsAdmin(true);
          bootstrapKeyRef.current = bootstrapKey;
          return;
        }

        if (!resolvedCompanyId) {
          setAttendantData({
            id: session?.attendant_id ?? null,
            name: session?.name ?? '',
            email: session?.email ?? '',
            company_id: null,
            role_id: sessionRoleId,
            role: sessionRoleName ? { id: sessionRoleId, name: sessionRoleName } : undefined,
            permission_ids: session?.permission_ids || [],
            permissions: session?.permissions || [],
          });
          setIsSuperAdmin(false);
          setIsAgent(sessionIsAgent);
          setIsGlobalAdmin(sessionIsGlobalAdmin);
          setIsAdmin(sessionIsAdmin);
          bootstrapKeyRef.current = bootstrapKey;
          return;
        }

        const me = await getAttendantMe();
        if (isCancelled) return;
        await applyAttendantData(me);
        if (isCancelled) return;

        bootstrapKeyRef.current = bootstrapKey;
      } catch (err) {
        if (isCancelled) return;
        const status = err?.response?.status ?? err?.status;
        if (status === 401) {
          performLogout();
          setAuthState({ attendantId: null, companyId: null, roleName: null, roleId: null });
          setAttendantData(null);
          setIsSuperAdmin(false);
          setIsAgent(false);
          setIsGlobalAdmin(false);
          setIsAdmin(false);
          applyRoleSnapshot(null, null);
          bootstrapKeyRef.current = buildBootstrapKey(hasReadableSessionCookie(), null, null);
          return;
        }
        console.error('Erro ao obter sessão/atendente.', {
          ...getSafeErrorDetails(err),
          ...(Number.isFinite(Number(status)) ? { status: Number(status) } : {}),
        });
        setAttendantData(null);
        setIsSuperAdmin(false);
        setIsAgent(false);
        setIsGlobalAdmin(false);
        setIsAdmin(false);
        applyRoleSnapshot(null, null);
        bootstrapKeyRef.current = null;
      } finally {
        if (!isCancelled) {
          setIsAuthReady(true);
        }
        bootstrapInFlightRef.current = false;
      }
    }

    bootstrapAttendant();

    return () => {
      isCancelled = true;
    };
  }, [
    applyRoleSnapshot,
    applyAttendantData,
    authState?.attendantId,
    authState?.companyId,
  ]);

  useEffect(() => {
    let isCancelled = false;

    if (!effectiveCompanyId) {
      setCompanySettings(null);
      return undefined;
    }
    if (bootstrapInFlightRef.current) {
      return undefined;
    }
    const currentSettingsCompanyId = normalizeOptionalNumber(
      companySettings?.id ?? companySettings?.company_id ?? null
    );
    if (currentSettingsCompanyId !== null && currentSettingsCompanyId === normalizeOptionalNumber(effectiveCompanyId)) {
      return undefined;
    }

    const fetchSettings = async () => {
      const settings = await requestCompanySettings();
      if (!isCancelled) {
        setCompanySettings(settings);
      }
    };

    fetchSettings();

    return () => {
      isCancelled = true;
    };
  }, [companySettings, effectiveCompanyId, requestCompanySettings]);

  useEffect(() => {
    const handleCompanySettingsUpdated = (event) => {
      const targetCompanyId =
        event?.detail?.companyId ??
        event?.detail?.company_id ??
        effectiveCompanyId;
      refreshCompanySettings(targetCompanyId, { force: true });
    };

    window.addEventListener('bluezap:company-settings-updated', handleCompanySettingsUpdated);
    return () => {
      window.removeEventListener('bluezap:company-settings-updated', handleCompanySettingsUpdated);
    };
  }, [effectiveCompanyId, refreshCompanySettings]);

  useEffect(() => {
    const handleAttendantUpdated = (event) => {
      const targetId = event?.detail?.attendantId ?? null;
      if (targetId && normalizedAttendantId !== null && Number(targetId) !== normalizedAttendantId) {
        return;
      }
      refreshAttendantData({ force: true });
    };

    window.addEventListener('bluezap:attendant-updated', handleAttendantUpdated);
    return () => {
      window.removeEventListener('bluezap:attendant-updated', handleAttendantUpdated);
    };
  }, [refreshAttendantData, normalizedAttendantId]);

  const allowPopupNotification = useMemo(
    () =>
      (companySettings?.active_popup_notification ?? true) &&
      (attendantData?.active_popup_notification ?? true),
    [companySettings, attendantData]
  );

  const allowSoundNotification = useMemo(
    () =>
      (companySettings?.active_sound_notification ?? true) &&
      (attendantData?.active_sound_notification ?? true),
    [companySettings, attendantData]
  );

  const allowInternalPopupNotification = useMemo(() => {
    const companyAllowed = true; // empresa sempre ativa para chat interno
    const attendantAllowed =
      attendantData?.active_internal_chat_popup_notification ??
      attendantData?.active_popup_notification ??
      true;
    return companyAllowed && attendantAllowed;
  }, [attendantData]);

  const allowInternalSoundNotification = useMemo(() => {
    const companyAllowed = true; // empresa sempre ativa para chat interno
    const attendantAllowed =
      attendantData?.active_internal_chat_sound_notification ??
      attendantData?.active_sound_notification ??
      true;
    return companyAllowed && attendantAllowed;
  }, [attendantData]);

  internalChatNotificationPrefsRef.current.allowPopupNotification = allowInternalPopupNotification;
  internalChatNotificationPrefsRef.current.allowSoundNotification = allowInternalSoundNotification;
  internalChatNotificationPrefsRef.current.attendantId = normalizedAttendantId;
  internalChatNotificationPrefsRef.current.isAdmin = Boolean(isAdmin);
  internalChatNotificationPrefsRef.current.adminScope = adminConversationScope;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isAuthReady || isPublicAuthRoute()) return;

    const popupNotificationsEnabled = allowPopupNotification || allowInternalPopupNotification;
    if (!popupNotificationsEnabled) return;
    if (!isNotificationSupported()) return;
    if (Notification.permission === 'granted') return;

    const storageKey = buildNotificationPermissionWarningKey(
      normalizedAttendantId,
      normalizedCompanyId
    );

    try {
      if (window.sessionStorage.getItem(storageKey) === '1') {
        return;
      }
      window.sessionStorage.setItem(storageKey, '1');
    } catch {
      // segue sem persistir o lembrete da sessão
    }

    showWarning('Notificações fora da tela dependem da permissão do navegador. Clique em "Ativar notificações" no header para liberar os alertas visuais.', {
      title: 'Permissão de notificações pendente',
      timeout: 7000,
    });
  }, [
    allowInternalPopupNotification,
    allowPopupNotification,
    isAuthReady,
    normalizedAttendantId,
    normalizedCompanyId,
  ]);

  useEffect(() => {
    const handleSync = (event) => {
      const payload = event.detail;
      if (payload?.type === 'chats_synced' && payload?.conversations) {
        handleChatsSynced(payload.conversations);
        return;
      }
      if (payload?.type === 'presence') {
        handleWhatsappPresenceEvent(payload);
      }
    };
    window.addEventListener('bluezap:conversation-event', handleSync);
    return () => {
      window.removeEventListener('bluezap:conversation-event', handleSync);
    };
  }, [handleChatsSynced, handleWhatsappPresenceEvent]);

  const focusChat = useCallback((chatId) => {
    window.focus?.();
    const hasChatId = chatId !== null && chatId !== undefined;
    if (window.location.pathname !== '/chatpage') {
      if (hasChatId) {
        try {
          sessionStorage.setItem('bluezap:pendingChatFocus', String(chatId));
        } catch {
          // Falha em sessionStorage nao deve impedir o redirecionamento.
        }
      }
      window.location.assign('/chatpage');
    } else if (hasChatId) {
      window.dispatchEvent(
        new CustomEvent('bluezap:focus-chat', {
          detail: { chatId },
        })
      );
    }
  }, []);


  const prevWaitingIdsRef = useRef(new Set());
  const prevMessageMetaRef = useRef({});
  const prevAttendingIdsRef = useRef(null);
  const waitingNotificationsReadyRef = useRef(false);
  const attendingNotificationsReadyRef = useRef(false);
  const attendingRealtimeAnchorTsRef = useRef(0);
  const sawInitialConversationLoadRef = useRef(false);
  const isWaitingSoundPlayingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleActiveChat = (event) => {
      const raw = event?.detail?.chatId;
      activeChatIdRef.current =
        raw === null || raw === undefined ? null : String(raw);
    };
    window.addEventListener('bluezap:active-chat', handleActiveChat);
    return () => {
      window.removeEventListener('bluezap:active-chat', handleActiveChat);
    };
  }, []);

  useEffect(() => {
    if (!chatClientsEnabled) {
      sawInitialConversationLoadRef.current = false;
      waitingNotificationsReadyRef.current = false;
      attendingNotificationsReadyRef.current = false;
      attendingRealtimeAnchorTsRef.current = 0;
      return;
    }
    if (loading || scopeLoading) {
      sawInitialConversationLoadRef.current = true;
    }
  }, [chatClientsEnabled, loading, scopeLoading]);

  useEffect(() => {
    if (!chatClientsEnabled) return;
    waitingNotificationsReadyRef.current = false;
    attendingNotificationsReadyRef.current = false;
    attendingRealtimeAnchorTsRef.current = 0;
    prevWaitingIdsRef.current = new Set();
    prevMessageMetaRef.current = {};
    prevAttendingIdsRef.current = null;
  }, [chatClientsEnabled, normalizedCompanyId, normalizedAttendantId, conversationScope]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleLogout = () => {
      waitingNotificationsReadyRef.current = false;
      attendingNotificationsReadyRef.current = false;
      attendingRealtimeAnchorTsRef.current = 0;
      sawInitialConversationLoadRef.current = false;
      prevWaitingIdsRef.current = new Set();
      prevMessageMetaRef.current = {};
      prevAttendingIdsRef.current = null;
      activeChatIdRef.current = null;
      if (isWaitingSoundPlayingRef.current) {
        stopNewClientNotificationSound();
        isWaitingSoundPlayingRef.current = false;
      }
    };

    window.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => {
      window.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, []);

  useEffect(() => {
    if (!chatClientsEnabled) {
      prevWaitingIdsRef.current = new Set();
      waitingNotificationsReadyRef.current = false;
      if (isWaitingSoundPlayingRef.current) {
        stopNewClientNotificationSound();
        isWaitingSoundPlayingRef.current = false;
      }
      return;
    }

    const waitingArray = Array.isArray(waitingList) ? waitingList : [];
    const currentIds = new Set(waitingArray.map((c) => c.id));
    const hasInitialConversationSnapshot =
      sawInitialConversationLoadRef.current && !loading && !scopeLoading;

    if (!waitingNotificationsReadyRef.current) {
      if (!hasInitialConversationSnapshot) {
        return;
      }
      prevWaitingIdsRef.current = currentIds;
      waitingNotificationsReadyRef.current = true;
      return;
    }

    const prevIds = prevWaitingIdsRef.current;
    const newClients = waitingArray.filter((client) => !prevIds.has(client.id));

    const orderedNewClients = newClients
      .map((client, index) => {
        const messages = Array.isArray(client?.messages) ? client.messages : [];
        const lastMessage = messages.length ? messages[messages.length - 1] : null;
        return {
          client,
          index,
          sortTs: resolveNotificationSortTs(lastMessage, client),
        };
      })
      .sort((left, right) => {
        if (left.sortTs !== right.sortTs) return left.sortTs - right.sortTs;
        return left.index - right.index;
      });

    orderedNewClients.forEach(({ client }) => {
      if (!allowPopupNotification && !allowSoundNotification) return;
      const contactName = resolveClientDisplayName(client) || 'Novo cliente';
      const numberLabel = resolveContactNumber(client);
      const messagePreview = getLastClientMessagePreview(client);
      const body = [numberLabel, messagePreview].filter(Boolean).join('\n');
      if (allowPopupNotification) {
        const avatarUrl = resolveClientAvatarUrl(client) || '/logo.png';
        emitPopupNotification(contactName, {
          body,
          icon: avatarUrl,
          attention: {
            enabled: true,
            kind: 'new-client',
            title: contactName,
          },
          onClick: (meta) => {
            trackAnalyticsEvent('notification_open_chat', {
              source: meta?.source || 'unknown',
              company_id: normalizedCompanyId,
              attendant_id: normalizedAttendantId,
              chat_id: client?.id ?? null,
              kind: 'waiting',
            });
            focusChat(client?.id ?? null);
          },
        });
      }
    });

    const audibleWaiting = waitingArray.filter((client) => {
      const handling = String(client?.handling_status ?? '').toLowerCase();
      if (handling.includes('waiting_attendant')) return true;
      const status = String(client?.status ?? '').toLowerCase();
      return status.includes('waiting_attendant');
    });

    const hasWaitingForAttendant = audibleWaiting.some((client) => {
      const slotAttendantId =
        client?.attendantId ?? client?.attendant_id ?? client?.attendant?.id ?? null;
      if (slotAttendantId === null || slotAttendantId === undefined) {
        return true;
      }

      if (normalizedAttendantId === null) {
        return false;
      }

      const parsed = Number(slotAttendantId);
      if (Number.isNaN(parsed)) {
        return false;
      }

      return parsed === normalizedAttendantId;
    });

    if (!allowSoundNotification || !hasWaitingForAttendant) {
      if (isWaitingSoundPlayingRef.current) {
        stopNewClientNotificationSound();
        isWaitingSoundPlayingRef.current = false;
      }
    } else if (!isWaitingSoundPlayingRef.current) {
      playNewClientNotificationSound(true);
      isWaitingSoundPlayingRef.current = true;
    }

    prevWaitingIdsRef.current = currentIds;
  }, [
    waitingList,
    normalizedAttendantId,
    normalizedCompanyId,
    allowPopupNotification,
    allowSoundNotification,
    chatClientsEnabled,
    emitPopupNotification,
    focusChat,
    loading,
    scopeLoading,
    conversationScope,
  ]);

  useEffect(() => {
    if (!chatClientsEnabled) {
      prevMessageMetaRef.current = {};
      prevAttendingIdsRef.current = null;
      attendingNotificationsReadyRef.current = false;
      attendingRealtimeAnchorTsRef.current = 0;
      return;
    }

    const attendingArray = Array.isArray(attendingList) ? attendingList : [];
    const waitingArray = Array.isArray(waitingList) ? waitingList : [];
    const trackedMap = new Map();
    [...attendingArray, ...waitingArray].forEach((chat) => {
      if (!chat || chat.id === null || chat.id === undefined) return;
      trackedMap.set(String(chat.id), chat);
    });
    const trackedArray = Array.from(trackedMap.values());
    const attendingIds = new Set(attendingArray.map((chat) => String(chat?.id)));
    const currentIds = new Set(trackedArray.map((chat) => chat.id));
    const hasInitialConversationSnapshot =
      sawInitialConversationLoadRef.current && !loading && !scopeLoading;

    if (!attendingNotificationsReadyRef.current) {
      if (!hasInitialConversationSnapshot) {
        return;
      }
      const seededMeta = {};
      trackedArray.forEach((chat) => {
        const messages = Array.isArray(chat?.messages) ? chat.messages : [];
        if (messages.length === 0) return;
        const lastMessage = messages[messages.length - 1];
        const currentTimestamp = getMessageActivityTimestamp(lastMessage) || null;
        const messageKey = resolveMessageKey(lastMessage);
        if (currentTimestamp || messageKey) {
          seededMeta[chat.id] = {
            key: messageKey || null,
            ts: currentTimestamp || 0,
          };
        }
      });
      prevMessageMetaRef.current = seededMeta;
      prevAttendingIdsRef.current = currentIds;
      attendingNotificationsReadyRef.current = true;
      attendingRealtimeAnchorTsRef.current = Date.now();
      return;
    }

    const prevIds = prevAttendingIdsRef.current;
    const isFirstSync = prevIds === null;
    const pendingMessageNotifications = [];
    trackedArray.forEach((chat) => {
      const messages = Array.isArray(chat?.messages) ? chat.messages : [];
      if (messages.length === 0) return;

      const lastMessage = messages[messages.length - 1];
      const isClient = Boolean(lastMessage && isClientMessage(lastMessage));

      const currentTimestamp = getMessageActivityTimestamp(lastMessage) || null;
      const messageKey = resolveMessageKey(lastMessage);
      const prevMeta = prevMessageMetaRef.current[chat.id] || null;
      const prevKey = prevMeta?.key || null;
      const prevTimestamp = prevMeta?.ts || null;
      const handlingStatus = String(chat?.handling_status || '').toLowerCase();
      const lastMessageHandlingStatus = String(lastMessage?.handling_status || '').toLowerCase();
      const isEvaluationConversation = EVALUATION_HANDLING_STATUSES.some((status) =>
        handlingStatus.includes(status)
      );
      const isEvaluationMessage = EVALUATION_HANDLING_STATUSES.some((status) =>
        lastMessageHandlingStatus.includes(status)
      );

      if (isEvaluationConversation || isEvaluationMessage) {
        if (currentTimestamp || messageKey) {
          prevMessageMetaRef.current[chat.id] = {
            key: messageKey || prevKey,
            ts: currentTimestamp || prevTimestamp || 0,
          };
        }
        return;
      }

      const hasPreviousSignal = Boolean(prevKey || prevTimestamp);
      const realtimeAnchorTs = Number(attendingRealtimeAnchorTsRef.current || 0);
      const hasFreshTimestamp =
        Number.isFinite(currentTimestamp) &&
        Number(currentTimestamp) >= realtimeAnchorTs - REALTIME_NOTIFICATION_GRACE_MS;
      const isLikelyHistoricalHydration =
        !hasPreviousSignal && !messageKey && !hasFreshTimestamp;
      const activeChatId = activeChatIdRef.current;
      const isActiveChat =
        activeChatId !== null &&
        activeChatId !== undefined &&
        String(activeChatId) === String(chat?.id);
      const isConversationOpen = isActiveChat && isPageVisible();
      const shouldSkipGlobalDedupe = () => {
        if (!messageKey || typeof window === 'undefined') return false;
        const now = Date.now();
        const map = readMessageDedupeMap();
        const changed = pruneMessageDedupeMap(map, now);
        const entry = map[String(chat?.id)];
        const recent =
          entry &&
          entry.key === messageKey &&
          Number(entry.ts || 0) &&
          now - Number(entry.ts || 0) <= MESSAGE_DEDUP_TTL_MS;
        if (changed) writeMessageDedupeMap(map);
        return Boolean(recent);
      };
      const markGlobalDedupe = () => {
        if (!messageKey || typeof window === 'undefined') return;
        const now = Date.now();
        const map = readMessageDedupeMap();
        pruneMessageDedupeMap(map, now);
        map[String(chat?.id)] = { key: messageKey, ts: now };
        writeMessageDedupeMap(map);
      };
      const alreadyNotified = shouldSkipGlobalDedupe();
      const notificationTrace = {
        conversation_id: chat?.id ?? null,
        chat_id: chat?.id ?? null,
        message_key: messageKey || null,
        timestamp: currentTimestamp || null,
        previous_message_key: prevKey || null,
        current_message_key: messageKey || null,
        previous_timestamp: prevTimestamp || null,
        current_timestamp: currentTimestamp || null,
        allowPopupNotification: Boolean(allowPopupNotification),
        pendingMessageNotifications: false,
        discarded_not_attending: false,
      };
      debugNotificationFlow('notification_detection_run', {
        conversation_id: chat?.id ?? null,
        previous_message_key: prevKey || null,
        current_message_key: messageKey || null,
        previous_timestamp: prevTimestamp || null,
        current_timestamp: currentTimestamp || null,
      });
      debugNotificationFlow('candidate_created', notificationTrace);
      const decision = shouldNotifyIncomingMessage(prevMeta, {
        key: messageKey || null,
        ts: currentTimestamp || 0,
      }, {
        chatId: chat?.id,
        initialSnapshot: isFirstSync || isLikelyHistoricalHydration,
        isClient,
        isStatusEvent: isStatusOnlyMessage(lastMessage),
        isEvaluation: isEvaluationConversation || isEvaluationMessage || isConversationAwaitingEvaluation(chat),
        isConversationOpen,
        alreadyNotified,
      });
      debugNotificationFlow('decision_evaluated', {
        ...notificationTrace,
        decision_notify: decision.notify,
        decision_reason: decision.reason,
        decision: {
          notify: decision.notify,
          reason: decision.reason,
        },
      });

      if (currentTimestamp || messageKey) {
        prevMessageMetaRef.current[chat.id] = {
          key: messageKey || prevKey,
          ts: currentTimestamp || prevTimestamp || 0,
        };
      }

      if (!decision.notify) {
        if (decision.reason === 'conversation_open') {
          markGlobalDedupe();
          markConversationRead(chat?.id, Date.now());
        }
        return;
      }

      if (decision.notify) {
        markGlobalDedupe();
        registerIncomingUnread({
          chatId: chat?.id,
          messageKey,
          occurredAt: Date.now(),
        });

        if (!attendingIds.has(String(chat?.id))) {
          debugNotificationFlow('notification_discarded_not_attending', {
            ...notificationTrace,
            decision_notify: decision.notify,
            decision_reason: decision.reason,
            discarded_not_attending: true,
          });
          return;
        }

        const contactName = resolveClientDisplayName(chat) || 'Cliente';
        const numberLabel = resolveContactNumber(chat);
        const messageText = extractMessagePreview(lastMessage);
        const body = [numberLabel, messageText].filter(Boolean).join('\n');

        pendingMessageNotifications.push({
          chat,
          contactName,
          body,
          messageKey,
          sortTs: currentTimestamp || Date.now(),
          icon: resolveClientAvatarUrl(chat) || '/logo.png',
        });
        debugNotificationFlow('queued', {
          ...notificationTrace,
          decision_notify: decision.notify,
          decision_reason: decision.reason,
          pendingMessageNotifications: true,
        });
      }

    });

    pendingMessageNotifications
      .sort((left, right) => {
        if (left.sortTs !== right.sortTs) return left.sortTs - right.sortTs;
        const leftId = Number(left.chat?.id || 0);
        const rightId = Number(right.chat?.id || 0);
        return leftId - rightId;
      })
      .forEach(({ chat, contactName, body, icon, messageKey }) => {
        if (allowPopupNotification) {
          emitPopupNotification(contactName, {
            body,
            icon,
            attention: {
              enabled: true,
              kind: 'message',
              title: contactName,
            },
            debugMeta: {
              conversationId: chat?.id ?? null,
              chatId: chat?.id ?? null,
              messageKey: messageKey || null,
              kind: 'message',
            },
            onClick: (meta) => {
              trackAnalyticsEvent('notification_open_chat', {
                source: meta?.source || 'unknown',
                company_id: normalizedCompanyId,
                attendant_id: normalizedAttendantId,
                chat_id: chat?.id ?? null,
                kind: 'message',
                message_key: messageKey || null,
              });
              focusChat(chat?.id ?? null);
            },
          });
        }
      });

    if (allowSoundNotification && pendingMessageNotifications.length > 0) {
      playNewMessageNotificationSound();
    }

    prevAttendingIdsRef.current = currentIds;
  }, [
    attendingList,
    waitingList,
    normalizedAttendantId,
    normalizedCompanyId,
    allowPopupNotification,
    allowSoundNotification,
    chatClientsEnabled,
    emitPopupNotification,
    focusChat,
    isPageVisible,
    loading,
    markConversationRead,
    registerIncomingUnread,
    scopeLoading,
    conversationScope,
  ]);

  useEffect(
    () => () => {
      if (isWaitingSoundPlayingRef.current) {
        stopNewClientNotificationSound();
        isWaitingSoundPlayingRef.current = false;
      }
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      attendantId,
      normalizedAttendantId,
      attendantData,
      companySettings,
      refreshCompanySettings,
      refreshAttendantData,
      refreshSession,
      isSuperAdmin,
      isAgent,
      isGlobalAdmin,
      isAdmin,
      isAuthReady,
      sessionHealth,
      isSessionOffline,
      isSessionInvalid,
      canAccessInternalChat,
      adminConversationScope,
      adminShowAllConversations,
      setAdminConversationScope,
      chatSortMode,
      setChatSortMode,
      allowPopupNotification,
      allowSoundNotification,
      allowInternalPopupNotification,
      allowInternalSoundNotification,
      attendantWsConnected: isAttendantWsConnected,
      setAttendantWsHandler,
      waitingList,
      attendingList,
      allList,
      clientContacts,
      loading,
      scopeLoading,
      error,
      refetch,
      applyIncomingPayload,
      lastOpenMap,
      incomingUnreadMap,
      markConversationRead,
      getConversationUnreadCount,
      fetchClientContacts,
      internalChatAttendants,
      internalChatAttendantsLoading,
      ensureInternalChatAttendantsLoaded,
      internalChatPresence,
      internalChatAttendanceMentions,
      clearInternalChatAttendanceMention,
      whatsappPresence,
      companyId: normalizedCompanyId,
      hasCompany,
    }),
    [
      attendantId,
      normalizedAttendantId,
      attendantData,
      companySettings,
      refreshCompanySettings,
      refreshAttendantData,
      refreshSession,
      isSuperAdmin,
      isAgent,
      isGlobalAdmin,
      isAdmin,
      isAuthReady,
      sessionHealth,
      isSessionOffline,
      isSessionInvalid,
      canAccessInternalChat,
      adminConversationScope,
      adminShowAllConversations,
      setAdminConversationScope,
      chatSortMode,
      setChatSortMode,
      allowPopupNotification,
      allowSoundNotification,
      allowInternalPopupNotification,
      allowInternalSoundNotification,
      isAttendantWsConnected,
      setAttendantWsHandler,
      waitingList,
      attendingList,
      allList,
      clientContacts,
      loading,
      scopeLoading,
      error,
      refetch,
      applyIncomingPayload,
      lastOpenMap,
      incomingUnreadMap,
      markConversationRead,
      getConversationUnreadCount,
      fetchClientContacts,
      internalChatAttendants,
      internalChatAttendantsLoading,
      ensureInternalChatAttendantsLoaded,
      internalChatPresence,
      internalChatAttendanceMentions,
      clearInternalChatAttendanceMention,
      whatsappPresence,
      normalizedCompanyId,
      hasCompany,
    ]
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
}
